/**
 * BlueprintTab — Portal feature map / developer reference
 * Self-contained documentation baked into Admin Panel.
 * Admin can quickly look up what exists, which files power it, pricing, and business rules.
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Search, BookOpen, Shield, CreditCard, Trophy, Rocket, FileCheck,
  Image as ImageIcon, HeadphonesIcon, BarChart3, Server, Zap, FolderGit2,
  Wallet, Users, Terminal, ChevronDown, ChevronRight, Mail,
} from 'lucide-react';

// =====================================================================
// FEATURE MAP — the source of truth for the portal
// Add new features here whenever something new ships.
// =====================================================================
const BLUEPRINT = [
  // ─────────── 🔐 AUTH ───────────
  {
    category: 'auth',
    title: 'Authentication & Security',
    icon: Shield,
    color: 'emerald',
    features: [
      {
        name: 'Email + Password Registration',
        description: 'JWT-based signup with 12+ char password, Indian mobile validation (or 11+ digit international).',
        backend: ['backend/app/routers/auth.py → register_user()', 'backend/app/models.py → UserCreate / validate_password_strength / validate_mobile_number'],
        frontend: ['frontend/src/pages/Register.jsx', 'frontend/src/context/AuthContext.jsx'],
        apis: ['POST /api/auth/register', 'POST /api/auth/check-email'],
      },
      {
        name: 'Login + JWT',
        description: 'Bcrypt password verification, JWT token with 30-day expiry. Email normalized to lowercase. On successful login, writes last_login (tz-aware UTC) and last_login_at (tz-aware UTC — used by the reengagement loop anti-spam skip_if_active_hours check) to db.users.',
        backend: ['backend/app/routers/auth.py → login()', 'backend/app/security.py → create_access_token'],
        frontend: ['frontend/src/pages/Login.jsx', 'frontend/src/context/AuthContext.jsx'],
        apis: ['POST /api/auth/login', 'GET /api/auth/verify'],
      },
      {
        name: 'Forgot / Reset Password',
        description: 'Token-based password reset with email link (token valid 30 min). Prevents enumeration attacks.',
        backend: ['backend/app/routers/auth.py → forgot_password / reset_password'],
        frontend: ['frontend/src/pages/ForgotPassword.jsx', 'frontend/src/pages/ResetPassword.jsx'],
        apis: ['POST /api/auth/forgot-password', 'POST /api/auth/reset-password'],
      },
      {
        name: 'Admin Panel Auth',
        description: 'Separate username/password login (env-sourced). JWT with type=admin. 30-min expiry.',
        backend: ['backend/app/routers/admin.py → admin_login / admin_verify', 'env: ADMIN_USERNAME, ADMIN_PASSWORD'],
        frontend: ['frontend/src/pages/AdminLogin.jsx', 'frontend/src/components/admin/adminUtils.js'],
        apis: ['POST /api/admin/login', 'GET /api/admin/verify'],
      },
      {
        name: 'Security Middleware',
        description: 'Rate limiting, request logging, brute-force protection, suspicious activity alerts.',
        backend: ['backend/app/security_middleware.py', 'backend/app/security.py'],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        name: 'Registration Anti-Abuse — 4-Layer Bot Defence (May 2026)',
        description: '🛡️ Designed after we realised the /register form had ZERO bot protection — a single Python loop could create unlimited fake accounts, flood Microsoft 365 SMTP quota (10k emails/day) within minutes, and self-inflict another silent outage. Now every registration runs through 4 INDEPENDENT layers in cheapest-first order — even if one is bypassed, the others still bite.\n\n🪤 LAYER 3 — Honeypot + min-fill-time (zero I/O, kills dumb bots first): hidden `<input name="website">` rendered off-screen with `aria-hidden tabIndex={-1}` — real users never see/touch it, bots that auto-fill every named input trip it. Combined with `form_started_at` timestamp captured on Register page mount via useRef(Date.now()) — submit < 2 sec after mount is rejected as bot-speed.\n\n🗑️ LAYER 4 — Disposable email blocklist: frozenset of ~80 known throw-away providers (mailinator/tempmail/guerrillamail/10minutemail/yopmail/etc., curated from ivolo/disposable-email-domains). O(1) lookup, exact domain match on lowercase email.\n\n⏱️ LAYER 2 — Per-IP rate limit (MongoDB-backed sliding window so it works across multiple uvicorn workers): max 3 successful registrations per IP per hour AND max 10 total attempts (including failures) per IP per hour. Every attempt persisted to `registration_attempts` collection {ip, email, outcome, reason, attempted_at} with 7-day TTL for self-cleanup.\n\n🔐 LAYER 1 — Cloudflare Turnstile invisible CAPTCHA (last because it requires an outbound HTTP call): @marsidev/react-turnstile rendered in Register.jsx with `size: invisible` / `appearance: interaction-only` — 99% of real users NEVER see a checkbox. Backend verifies token against challenges.cloudflare.com/turnstile/v0/siteverify via async httpx with 10 sec timeout. Smart fallback: if TURNSTILE_SECRET_KEY env var unset → logs ONE warning + skips Layer 1 (dev-friendly); if set to Cloudflare\'s always-pass test secret → every token passes (staging/CI); if set to real prod secret → strict verification.\n\n✅ Production verified via curl: bot without token → 400 "Please complete the security check"; mailinator → 400 "Please use a permanent email address"; honeypot filled → 400 "Invalid form submission"; submit < 2 sec → 400 "Form submitted too quickly"; 4th registration from same IP within 1h → 429 "Too many accounts created"; legit registration with valid token → 200 with JWT.',
        backend: [
          'backend/app/services/registration_security.py (~280 lines, lint-clean)',
          'backend/app/routers/auth.py → register_user() (guards run BEFORE any DB lookup)',
          'backend/app/models.py → UserCreate extended with turnstile_token / website / form_started_at (all optional → old clients still work)',
          'collection: registration_attempts → {ip, email, outcome, reason, attempted_at} with 7-day TTL',
          'env: TURNSTILE_SECRET_KEY (backend), REACT_APP_TURNSTILE_SITEKEY (frontend)',
        ],
        frontend: [
          'frontend/src/pages/Register.jsx → honeypot input + Turnstile widget + formStartedAtRef',
          'package: @marsidev/react-turnstile@1.5.2',
        ],
        apis: [
          'POST /api/auth/register (now hardened against bots)',
        ],
      },
      {
        name: 'Login Brute Force Protection (May 17, 2026)',
        description: '🔒 Prevents credential-stuffing attacks on the /api/auth/login endpoint. After 5 failed login attempts from the same email OR IP address within a sliding window, the account is locked for 15 minutes. Lockout is stored in MongoDB (login_attempts collection) — persists across server restarts and works across multiple uvicorn workers. Returns HTTP 429 with a clear message: "Too many failed attempts. Try again in X minutes." Successful login resets the counter for that email+IP pair. Admin login endpoint is unaffected (separate route).',
        backend: [
          'backend/app/routers/auth.py → login() (brute-force guard runs BEFORE password check)',
          'collection: login_attempts → {email, ip, attempts, locked_until, last_attempt_at}',
          'threshold: 5 attempts → 15-min lockout per email+IP pair',
        ],
        frontend: ['—'],
        apis: ['POST /api/auth/login (now rate-limited per email+IP)'],
      },
      {
        name: 'Admin Password Bcrypt Hashing (May 17, 2026)',
        description: '🔐 Admin panel password was previously stored as plain text in the ADMIN_PASSWORD env variable and compared with plain == equality. Replaced with bcrypt: on startup the backend reads ADMIN_PASSWORD_HASH (bcrypt hash) and verifies using bcrypt.checkpw(). Backwards-compatible fallback: if ADMIN_PASSWORD_HASH is not set, falls back to ADMIN_PASSWORD plain-text comparison (so existing deployments are not broken). Production setup: generate hash once with `python -c "import bcrypt; print(bcrypt.hashpw(b\'yourpass\', bcrypt.gensalt()).decode())"` and set ADMIN_PASSWORD_HASH in .env.',
        backend: [
          'backend/app/routers/admin.py → admin_login() — bcrypt.checkpw() verification',
          'env: ADMIN_PASSWORD_HASH (bcrypt hash, preferred) or ADMIN_PASSWORD (plain text, fallback)',
          'pkg: bcrypt (already in requirements.txt for user passwords)',
        ],
        frontend: ['—'],
        apis: ['POST /api/admin/login'],
      },
      {
        name: 'Register + Navbar UX Fixes (May 16, 2026)',
        description: '🛠️ Two small but user-facing fixes shipped together.\n\n📋 Register.jsx — first_name field hardening: (1) Placeholder changed from "John" to "Enter first name only (e.g. Ravi)" so intent is immediately clear. (2) Soft helper text "Please enter your first name only, not full name" shown below the field when no error is active; replaced by the red error message when validation triggers. (3) Space-detection validation added to validateField() — if first_name contains a space, shows "Please enter first name only" immediately on keystroke, preventing students from typing full names like "Ravi Kumar" into the first-name field.\n\n📱 Navbar.jsx — mobile drawer long-name truncation: the "Welcome, {first_name}" greeting in the mobile hamburger menu drawer had no overflow control — a long name like "Alexandros" would push past the drawer edge. Fixed by wrapping the greeting in a flex container with min-w-0 and adding a truncate span, matching the same pattern already used in the desktop nav.',
        backend: ['—'],
        frontend: [
          'frontend/src/pages/Register.jsx → validateField() first_name case: added space-detection branch',
          'frontend/src/pages/Register.jsx → first_name Input: placeholder updated',
          'frontend/src/pages/Register.jsx → helper/error swap: shows grey hint when no error, red error when validation fails',
          'frontend/src/components/Navbar.jsx → mobile user info section (~line 265): flex + min-w-0 + truncate span',
        ],
        apis: ['—'],
      },
    ],
  },

  // ─────────── 🎓 COURSES & LABS ───────────
  {
    category: 'courses',
    title: 'Courses & Labs (251 labs · 16 courses)',
    icon: BookOpen,
    color: 'indigo',
    features: [
      {
        name: 'YAML-driven Lab Engine',
        description: '251 labs across 16 courses — each lab is a YAML spec file. Adding new lab = adding YAML, no code change.',
        backend: ['backend/app/specs_loader.py', 'backend/specs/<course>/<lab>.yaml'],
        frontend: ['frontend/src/pages/CourseLabs.jsx', 'frontend/src/pages/LabDetail.jsx'],
        apis: ['GET /api/courses', 'GET /api/courses/{code}/labs', 'GET /api/labs/{long_id}'],
      },
      {
        name: 'Course Catalog',
        description: '13 paid courses + 3 exam attempts. Includes Linux (RH124/RH134/SVRADM), DevOps (Docker/Ansible/K8s/Terraform/Git), Programming (Python/C/C++/Java), DB (MySQL).',
        backend: ['backend/app/specs_loader.py → course_titles dict (line 119)'],
        frontend: ['frontend/src/constants/courseData.js', 'frontend/src/pages/Courses.jsx'],
        apis: ['GET /api/courses'],
      },
      {
        name: 'VM-based Lab Verification',
        description: 'Student runs lab on real KVM VM. Backend executes pytest scripts against VM IP, records pass/fail + score. Anti-cheat: (user_id, lab_long_id) is unique.',
        backend: ['backend/app/routers/verify.py', 'backend/tests/<course>/<lab>/test_*.py'],
        frontend: ['frontend/src/pages/LabDetail.jsx → runVerification()'],
        apis: ['POST /api/verify/{course}/{lab_id}', 'GET /api/progress/my'],
      },
      {
        name: 'Progress Tracking',
        description: 'Per-user per-lab: status (pass/fail/pending), score, attempts, last_attempt. Dashboard shows per-course % completion.',
        backend: ['backend/app/routers/progress.py', 'db collection: progress'],
        frontend: ['frontend/src/pages/Dashboard.jsx', 'frontend/src/pages/CourseLabs.jsx (progress bar)'],
        apis: ['GET /api/progress/my', 'GET /api/progress/course/{code}'],
      },
      {
        name: 'Command Hints in Labs',
        description: 'Per-task hints (GIT, RH124, RH134, SVRADM, ANSIBLE, DOCKER, TERRAFORM, KUBERNETES). Optional pending: EXAM_RHCSA, C, CPP, PYTHON, JAVA.',
        backend: ['YAML hints field in spec files'],
        frontend: ['frontend/src/pages/LabDetail.jsx → hint reveal'],
        apis: ['—'],
      },
      {
        name: 'Lab Verification Fix Log — Silent Patches (May 2026)',
        description: '🔍 Set of lab-verification bug fixes where the hint command was correct but the test assertion was too strict, causing students to fail despite running the right command. All fixes done via TEST FILE changes only (no title/spec change) so per blueprint rule scores are PRESERVED — students do not have to redo anything, next "Verify" click immediately credits them.\n\n🩹 Bugs fixed:\n  • RH134 lab17 q1 — Old test: `grep student ~/lab17_q1.txt` fail. Reason: `ps -u student > ~/lab17_q1.txt` output has only PID/TTY/TIME/CMD columns, the literal word "student" is NEVER in the output. New test: file exists + non-empty + first line contains `PID` or `UID` header (accepts every standard `ps` variant — `ps -u`, `ps -fu`, `ps aux`, `ps -ef --user`).\n  • RH124 lab41 q6, ANSIBLE lab11 q1, RH134 lab19 q1-q8, RH124 lab35 q4 & q6 — earlier same-pattern fixes (per March handoff). All changes silent: scores intact, students unblocked instantly.\n\n📌 Rule documented for future maintainers: changing a lab\'s TITLE wipes student progress for that task (deliberately, so they\'re notified of substantive changes). Changing TEST or HINT is silent. Fix bad verifications via test-file edits, never title edits.',
        backend: [
          'backend/tests/RH134/lab17/test_q1.py (rewritten to PID|UID header check)',
        ],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        name: 'Free vs Paid Access Rules',
        description: '🔓 Freemium access model — exactly which labs/tests are free vs locked.\n\n📚 COURSE LABS:\n  • RH124 (Linux Fundamentals): lab1, lab2, lab3 free — 3 of ~42 labs\n  • C_LANG (C Programming): lab1, lab2 free — 2 of ~15 labs\n  • ALL other courses (RH134, SVRADM, ANSIBLE, DOCKER, KUBERNETES, TERRAFORM, GIT, PYTHON, CPP, MYSQL, JAVA): 0 free labs — all locked until purchase\n\n📝 MOCK TESTS:\n  • Only RH124 Test 1 (ID 101) is free — the very first test in the catalog\n  • All other 52 tests (IDs 102–1503, across all 15 categories) require Mock Tests Bundle purchase (₹499/$19)\n\n📦 PROJECTS:\n  • Per-project is_free field in db.projects — admin can toggle any project free/paid via /toggle-free endpoint\n  • Default is_free=False (paid ₹99/$2)\n\n⚠️ FRONTEND DUPLICATE WARNING: CourseLabs.jsx lines 500–503 has a HARDCODED copy of the free lab list (RH124 lab1-3, C_LANG lab1-2). This is client-side visual only — real enforcement is backend. If FREE_LABS is ever updated in access_control.py, CourseLabs.jsx must be manually updated to match. These two MUST stay in sync.',
        backend: [
          'backend/app/services/access_control.py → FREE_LABS dict (source of truth for lab access)',
          'backend/mock_test_questions_bank.py → FREE_MOCK_TESTS = {101} (only RH124 Test 1)',
          'backend/app/routers/lab.py → enforces FREE_LABS on GET /lab/list/{course} and GET /lab/{course}/{lab_id}',
          'backend/app/routers/verify.py → check_lab_enrollment() passes free labs without DB query',
          'backend/app/routers/projects.py → reads is_free per document from db.projects',
          'backend/app/routers/mock_tests.py → is_test_free() gates test start and catalog display',
        ],
        frontend: [
          'frontend/src/pages/CourseLabs.jsx lines 500–503 → HARDCODED isFreeLab check (must mirror FREE_LABS)',
        ],
        apis: [
          'GET /api/lab/list/{course} — strips tasks[] for locked labs when not enrolled',
          'GET /api/lab/{course}/{lab_id} — 403 for locked labs if not enrolled',
          'POST /api/admin/projects/{id}/toggle-free — toggles is_free per project',
        ],
      },
    ],
  },

  // ─────────── 🖥️ VM INFRASTRUCTURE ───────────
  {
    category: 'vm',
    title: 'VM Infrastructure (KVM-based)',
    icon: Terminal,
    color: 'slate',
    features: [
      {
        name: 'VM Session Lifecycle',
        description: 'Start VM → get IP → expire after 2 hours → auto-cleanup. Session timeout configurable via env.',
        backend: ['backend/app/routers/vm_terminal.py', 'backend/app/services/storage_lab_service.py', 'env: SESSION_TIMEOUT_MINUTES'],
        frontend: ['frontend/src/pages/LabDetail.jsx → VM controls'],
        apis: ['POST /api/vm/start', 'POST /api/vm/stop', 'GET /api/vm/status'],
      },
      {
        name: 'Web Terminal (WebSocket)',
        description: 'Browser-based SSH terminal into VM. xterm.js + websocket proxy.',
        backend: ['backend/app/routers/vm_terminal.py → websocket endpoint'],
        frontend: ['frontend/src/pages/LabDetail.jsx → xterm integration'],
        apis: ['WS /api/vm/terminal/{session_id}'],
      },
      {
        name: 'PPS Network Monitoring',
        description: '3-tier alerts (warning/critical/emergency) every 5 sec. Auto-kill VM on 25000+ PPS. Email alerts to admin. VM audit log (who started what, when).',
        backend: ['backend/app/services/pps_monitor.py', 'backend/app/routers/pps_monitoring.py', 'db: vm_audit_log'],
        frontend: ['admin → Network tab (NetworkTab.jsx)'],
        apis: ['GET /api/admin/pps/status', 'GET /api/admin/pps/audit-log'],
      },
      {
        name: 'VM Template / Clone Script',
        description: 'Secure cloning of golden image per user. Per-user port allocation. KVM hypervisor.',
        backend: ['backend/app/services/storage_lab_service.py', 'env: CONTAINER_IMAGE, CONTAINER_PORT_RANGE'],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        name: 'Lab VM Auto-Cleanup (Zombie Reaper)',
        description: 'Two complementary systemd timers running every 5 min on the prod host that together cover ALL zombie VM scenarios. (1) Shut-off zombie reaper (`grras-lab-vm-cleanup`): removes stale `lab-*` VMs in shut-off state for 15+ min that hold disk references and block new VM creation with "path_in_use" errors (e.g. Manvendra ticket #23). (2) Running-orphan reaper (`grras-lab-vm-orphan-cleanup`, NEW): cross-references libvirt running `lab-*` VMs against MongoDB `vm_sessions` collection — if a VM is running but has NO matching record (DB insert failed mid-flight, or session ended without VM destroy), it gets reaped after 10 min grace period. Both scripts share `/var/log/grras-lab-vm-cleanup.log` (orphan entries prefixed with `[orphan]`). MongoDB-unreachable case: orphan script aborts safely, no VMs touched. Protects running tracked VMs and system VMs (exam-server, lab-template, student-node, exam-server-backup) via regex + state + age + DB-tracking gates. Boot-persistent. Applies to ALL course VMs (RH124, RH134, DOCKER, ANSIBLE, EXAM_*, KUBERNETES, etc).',
        backend: [
          'prod-host: /usr/local/bin/grras-lab-vm-cleanup (bash) — shut-off scenario',
          'prod-host: /usr/local/bin/grras-lab-vm-orphan-cleanup (python3, queries vm_sessions) — running orphan scenario',
          'prod-host: /etc/systemd/system/grras-lab-vm-cleanup.{service,timer}',
          'prod-host: /etc/systemd/system/grras-lab-vm-orphan-cleanup.{service,timer}',
          'prod-host: /var/log/grras-lab-vm-cleanup.log (shared audit trail, both scripts)',
          'schedule: every 5 min, OnBootSec=2min/3min, AccuracySec=30s, Persistent=true',
          'shut-off filter: ^lab-[a-f0-9-]+$ + state=shut-off + age≥15min',
          'orphan filter: ^lab-[a-f0-9-]+$ + state=running + NOT in vm_sessions.vm_name + age≥10min',
        ],
        frontend: ['—'],
        apis: ['—'],
      },
    ],
  },

  // ─────────── 💰 PAYMENTS ───────────
  {
    category: 'payments',
    title: 'Pricing & Payments (Razorpay)',
    icon: CreditCard,
    color: 'amber',
    features: [
      {
        name: 'Razorpay Live Integration',
        description: 'Creates orders in INR paise for India / USD cents for international. Bills international cards in USD directly (no ₹ conversion needed).',
        backend: ['backend/app/routers/payments.py → create_order / verify / webhook', 'env: RAZORPAY_KEY_ID, RAZORPAY_KEY_SECRET'],
        frontend: ['frontend/src/pages/Payment.jsx → handlePayment()'],
        apis: ['POST /api/payments/create-order', 'POST /api/payments/verify', 'POST /api/payments/webhook'],
      },
      {
        name: 'Dynamic All Access Pricing',
        description: 'Reads user\'s completed payments, credits actual amounts against ₹4999 base. Floor ₹499. International = flat $149 (no credit). Returns remaining_courses_only + remaining_exams_only split.',
        backend: ['backend/app/routers/payments.py → calculate_all_access_price() line 174'],
        frontend: ['frontend/src/pages/Pricing.jsx', 'frontend/src/pages/Dashboard.jsx (upsell widget)', 'frontend/src/pages/Payment.jsx'],
        apis: ['GET /api/payments/all-access-pricing'],
      },
      {
        name: 'Geo-based Currency Detection',
        description: 'useCurrency hook calls ip-api.com / ipapi.co. If countryCode=IN → isIndia=true (INR), else false (USD). Defaults to India if geolocation blocked.',
        backend: ['—'],
        frontend: ['frontend/src/hooks/useCurrency.js'],
        apis: ['external: ip-api.com / ipapi.co'],
      },
      {
        name: 'Smart Dashboard Upsell Widget',
        description: 'Shows "You\'ve invested ₹X · Level up for ₹Y more · Unlock N courses + M exams FREE" when user has credit & remaining items. India-only, dismissible (session-scoped).',
        backend: ['—'],
        frontend: ['frontend/src/pages/Dashboard.jsx line 537-585'],
        apis: ['uses /api/payments/all-access-pricing'],
      },
      {
        name: 'Per-Project Payment (₹99 / $2)',
        description: 'Flat price per real-world project. Course_id format: PROJECT_<ObjectId>. USD billed in cents.',
        backend: ['backend/app/routers/payments.py → is_project branch'],
        frontend: ['frontend/src/pages/Projects.jsx', 'frontend/src/pages/Payment.jsx'],
        apis: ['POST /api/payments/create-order with course_id=PROJECT_<id>'],
      },
      {
        name: 'Credit Breakdown Tooltip',
        description: '"Your Credit: -₹X ⓘ" on Pricing page. Hover reveals itemized list of past purchases that count toward credit.',
        backend: ['returns credited_items[] in /all-access-pricing'],
        frontend: ['frontend/src/pages/Pricing.jsx line 252-279'],
        apis: ['—'],
      },
      {
        name: 'Abandoned Cart Recovery (May 17, 2026)',
        description: '🛒 Automatic 2-email recovery sequence for pending (unpaid) Razorpay orders.\n\nEmail 1 — sent 1 hour after payment created_at if still pending: subject "You left something behind — {course_name} is waiting". Heading and benefits bullet points adapt to payment_type (course / all_access / exam / mock_test / project). Info table shows Course/Bundle + Amount highlighted in orange.\n\nEmail 2 — sent 23 hours after Email 1 if still pending: subject "Last reminder — {course_name} is still waiting for you". Same structure + testimonial quote box + total_students count for social proof + stronger CTA "Complete Enrollment Now →" + unsubscribe note.\n\nTracking: 4 new fields on Payment model (recovery_email_1_sent / _sent_at / recovery_email_2_sent / _sent_at). Background loop runs every 30 min (5-min startup delay). Counts total_courses and total_exam_sets are computed dynamically from COURSE_PRICING at runtime.',
        backend: [
          'backend/app/models.py → Payment model: recovery_email_1_sent / recovery_email_1_sent_at / recovery_email_2_sent / recovery_email_2_sent_at',
          'backend/app/services/email_service.py → send_abandoned_cart_email_1() + send_abandoned_cart_email_2()',
          'backend/app/services/cart_recovery.py → cart_recovery_loop() (every 30 min) + run_cart_recovery()',
          'backend/app/main.py → app.state.cart_recovery_task = asyncio.create_task(cart_recovery_loop())',
          'backend/app/templates/emails/abandoned_cart_1.html + abandoned_cart_2.html',
          'triggered_by="abandoned_cart_recovery" (filterable in Email Activity tab)',
        ],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        name: 'Admin Payment Notification (May 17, 2026)',
        description: '💰 Admin receives an email alert on every successful payment captured via the Razorpay webhook (payment.captured event). Email shows: Student Name, Email, Course/Bundle name, Amount (formatted with currency symbol), Currency, Order ID, and Time UTC. Sent as inline HTML to ADMIN_NOTIFICATION_EMAIL (falls back to EMAIL_USER). Fired as a non-blocking asyncio background task so webhook response is never delayed. Uses the _BACKGROUND_TASKS strong-reference pattern from auth.py to prevent garbage collection.',
        backend: [
          'backend/app/services/email_service.py → send_payment_notification_to_admin()',
          'backend/app/routers/payments.py → _BACKGROUND_TASKS set + asyncio.create_task(_notify_admin()) inside payment.captured webhook handler',
          'triggered_by="payment_notification" (filterable in Email Activity tab)',
        ],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        name: 'Meta Pixel — Full Funnel Event Tracking (May 2026)',
        description: '📊 Originally only `CompleteRegistration` was firing → Meta ad-optimization could only target "people who register", not "people who PAY". Result: ads optimized for signup volume, not revenue. After expanding to a full 5-event funnel, Meta\'s ad-auction model can train on actual purchase signals → industry benchmark says 40-60% drop in cost-per-paying-customer once ~50 Purchase events accumulate.\n\n🎯 5 Standard Events wired into the existing pixel:\n  1. ViewContent  → fires on every course detail page (CourseLabs.jsx). Sends `content_ids: [courseId]` + `content_name`. Used by Meta to build interest-based lookalike audiences.\n  2. Lead         → fires ONCE per browser when user opens their first lab terminal (LabTerminal.jsx). Deduplicated via localStorage key `meta_lead_first_lab_fired_v1` so refreshes / second-lab visits don\'t dilute the signal. Marks the user as "real intent" to Meta.\n  3. AddToCart    → fires on every pricing page visit (Pricing.jsx). Repeat visits are stronger purchase-intent, hence intentional re-fire.\n  4. InitiateCheckout → fires when courseInfo is fully resolved on Payment.jsx with a valid INR price > 0. Sends `value` + `currency: "INR"` + `content_ids: [courseId]`.\n  5. Purchase     → fires INSIDE Razorpay handler AFTER `verifyResponse.success === true` BUT BEFORE `navigate("/payment-success")`. Critical placement: firing on PaymentSuccess.jsx mount could double-fire on F5 / share. With this placement, exactly one Purchase event per verified payment. Sends `value` (courseInfo.priceINR) + `currency: "INR"` + `order_id` (Razorpay payment_id).\n\n🛡️ Centralised helper `frontend/src/utils/metaPixel.js` — defensive `safeFbq()` guard checks `window.fbq` exists, try/catch around every call, console.log for in-browser verification, never crashes app if pixel blocked. All 5 helpers exported for reuse from anywhere.\n\n✅ Existing infra reused: Meta Pixel base code already in `index.html` <head>, `CompleteRegistration` still fires in AuthContext on signup, no breaking changes to existing flow.',
        backend: ['—'],
        frontend: [
          'frontend/src/utils/metaPixel.js → safeFbq wrapper + 5 exported trackers',
          'frontend/src/pages/CourseLabs.jsx → ViewContent on course param change',
          'frontend/src/pages/LabTerminal.jsx → Lead once per browser (localStorage gated)',
          'frontend/src/pages/Pricing.jsx → AddToCart on every visit',
          'frontend/src/pages/Payment.jsx → InitiateCheckout + Purchase (inline in Razorpay handler)',
        ],
        apis: ['— (client-side only; server-side Conversions API is a future P3 addition)'],
      },
    ],
  },

  // ─────────── 🏆 LEADERBOARD ───────────
  {
    category: 'gamification',
    title: 'Leaderboard & Gamification',
    icon: Trophy,
    color: 'yellow',
    features: [
      {
        name: 'Privacy-First Top 5 Leaderboard',
        description: 'Only top 5 names visible. Rivals shown as anonymous handles ("Linux Learner #A8B"). Total student count hidden — brand protection.',
        backend: ['backend/app/routers/analytics.py → _build_leaderboard, public_leaderboard, /champions'],
        frontend: ['frontend/src/pages/Leaderboard.jsx', 'frontend/src/components/RivalsStrip.jsx'],
        apis: ['GET /api/leaderboard/top', 'GET /api/leaderboard/champions', 'GET /api/leaderboard/rivals'],
      },
      {
        name: 'Tier System (6 tiers)',
        description: 'Champion > Diamond > Gold > Silver > Bronze > Starter. Based on rank_score = labs × 100 + total_score.',
        backend: ['backend/app/routers/analytics.py → _TIERS constant'],
        frontend: ['frontend/src/pages/Leaderboard.jsx → tier badge + progress bar'],
        apis: ['included in /champions response'],
      },
      {
        name: 'Anti-Cheat: Unique Attempts',
        description: '(user_id, lab_long_id) unique index in db.progress. Repeat attempts update the same doc, never inflate counts.',
        backend: ['backend/app/routers/verify.py → upsert logic', 'db index: progress (user_id, long_id)'],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        name: 'Streak Counter',
        description: 'Consecutive active days ending today/yesterday. 🔥 flame displayed.',
        backend: ['backend/app/routers/analytics.py → _compute_streak()'],
        frontend: ['leaderboard rows show streak_days'],
        apis: ['in /top and /champions'],
      },
    ],
  },

  // ─────────── 📦 PROJECTS HUB ───────────
  {
    category: 'projects',
    title: 'Real-World Projects Hub',
    icon: FolderGit2,
    color: 'rose',
    features: [
      {
        name: 'Stepper / Wizard UI',
        description: 'Section-by-section project walkthrough. "Mark as Done" tracking. Lifetime access after ₹99/$2 purchase.',
        backend: ['backend/app/routers/projects.py'],
        frontend: ['frontend/src/pages/Projects.jsx', 'frontend/src/pages/ProjectDetail.jsx'],
        apis: ['GET /api/projects', 'GET /api/projects/{id}', 'POST /api/projects/{id}/complete'],
      },
      {
        name: '12 Seeded Projects',
        description: 'Linux, DevOps, Programming, Database categories. Seeded via scripts.',
        backend: ['backend/seed_project.py', 'db: projects collection'],
        frontend: ['displayed on /projects'],
        apis: ['GET /api/projects'],
      },
      {
        name: 'Admin Project Management',
        description: 'Add/edit/toggle projects, section-wise editor, free/paid toggle, grant per-user access.',
        backend: ['backend/app/routers/projects.py → /api/admin/projects endpoints'],
        frontend: ['admin → Projects tab (ProjectsTab.jsx)'],
        apis: ['POST /api/admin/projects', 'POST /api/admin/projects/{id}/toggle', '/toggle-free'],
      },
      {
        name: 'AWS Cloud & Solutions Architect Projects (May 2026)',
        description: 'Two AWS-category projects added to the Projects Hub: (1) AWS Cloud Practitioner Complete Course — beginner, category: AWS, covers IAM, EC2, S3, RDS, ELB, CloudWatch, billing. (2) AWS Solutions Architect Complete Course — intermediate, category: AWS, covers VPC, Aurora, Lambda, HA architecture, cost optimisation. Both sold at flat ₹99/$2 like all projects. Seeded via admin form — no code change required (payment flow is keyed entirely on course_id.startsWith("PROJECT_"), category is never read by payments/access_control).',
        backend: ['db: projects collection (seeded via admin Projects form)', 'backend/app/routers/payments.py → is_project branch handles all PROJECT_<ObjectId> IDs automatically'],
        frontend: ['frontend/src/pages/Projects.jsx', 'frontend/src/pages/ProjectDetail.jsx'],
        apis: ['GET /api/projects', 'POST /api/payments/create-order (course_id=PROJECT_<id>)'],
      },
    ],
  },

  // ─────────── 📝 MOCK TESTS ───────────
  {
    category: 'mocks',
    title: 'Mock Tests (Practice Exams)',
    icon: FileCheck,
    color: 'purple',
    features: [
      {
        name: 'Course-specific Mock Tests',
        description: 'Multi-choice practice exams per course. Timed, scored, results stored.',
        backend: ['backend/app/routers/mock_tests.py (if exists)', 'db: mock_test_attempts'],
        frontend: ['frontend/src/pages/MockTests.jsx'],
        apis: ['GET /api/mocks/{course}', 'POST /api/mocks/submit'],
      },
      {
        name: 'Mock Tests Bundle (₹499 / $19)',
        description: 'Sold separately from All Access. Excluded from bundle deliberately.',
        backend: ['backend/app/routers/payments.py → ALL_ACCESS_EXCLUDED = {"MOCK_TESTS", ...}'],
        frontend: ['frontend/src/pages/MockTests.jsx (gated by purchase)'],
        apis: ['—'],
      },
      {
        name: 'AWS Mock Tests — CCP + SAA (May 2026)',
        description: 'Two new mock test categories added to the portal. AWS Cloud Practitioner (CLF-C02): 4 tests, IDs 1401–1404, 80 questions across Cloud Fundamentals/IAM, EC2/S3/Storage, Databases/ELB/Auto Scaling, Networking/Billing/Well-Architected. AWS Solutions Architect Associate (SAA-C03): 3 tests, IDs 1501–1503, 60 real-world scenario questions across VPC/Security, Databases/Serverless/HA, and Cost Optimisation/Exam Scenarios. ALL_CATEGORIES updated to 15 entries; 53 total tests now loaded. Frontend shows amber/orange colour scheme for both AWS tiles with the AWS devicon logo.',
        backend: [
          'backend/mock_tests/aws_ccp_tests.py → AWS_CCP_MOCK_TESTS (IDs 1401–1404, 80 questions)',
          'backend/mock_tests/aws_saa_tests.py → AWS_SAA_MOCK_TESTS (IDs 1501–1503, 60 questions)',
          'backend/mock_test_questions_bank.py → imports + **AWS_CCP_MOCK_TESTS + **AWS_SAA_MOCK_TESTS; ALL_CATEGORIES now 15 entries',
        ],
        frontend: [
          'frontend/src/pages/MockTests.jsx → LOGO_URLS.AWS (cdn.jsdelivr.net devicon)',
          'frontend/src/pages/MockTests.jsx → COURSE_CONFIG.AWS_CCP (amber-500/orange-500)',
          'frontend/src/pages/MockTests.jsx → COURSE_CONFIG.AWS_SAA (orange-500/yellow-500)',
        ],
        apis: ['—'],
      },
    ],
  },

  // ─────────── 🎨 POSTER GENERATORS ───────────
  {
    category: 'posters',
    title: 'Marketing & Achievement Posters',
    icon: ImageIcon,
    color: 'fuchsia',
    features: [
      {
        name: 'Promo Poster (Admin only)',
        description: 'Auto-pull Top 3 (All-Time/Monthly/Weekly) from leaderboard → premium dark navy poster in Square/Story/Landscape. 2× Retina PNG export via html-to-image. Privacy toggle (First+Initial / Full Name).',
        backend: ['backend/app/routers/analytics.py → /podium endpoint'],
        frontend: ['frontend/src/components/admin/PromoPosterTab.jsx'],
        apis: ['GET /api/admin/analytics/podium'],
      },
      {
        name: 'Achievement Poster (Admin only)',
        description: 'Per-student course-completion poster. Dark navy + gold + SVG leaves. Photo + cert upload with zoom/pan sliders. AI-generated captions (GPT-5-nano, 3 tones).',
        backend: ['backend/app/routers/admin.py → /api/admin/poster/suggest-captions (emergentintegrations GPT-5-nano)'],
        frontend: ['frontend/src/components/admin/AchievementPosterTab.jsx', 'frontend/src/components/admin/certificateTemplates.js'],
        apis: ['POST /api/admin/poster/suggest-captions'],
      },
    ],
  },

  // ─────────── 🎧 SUPPORT ───────────
  {
    category: 'support',
    title: 'Help Center / Support Tickets',
    icon: HeadphonesIcon,
    color: 'sky',
    features: [
      {
        name: 'Ticket System',
        description: 'Students raise tickets with department (Billing/Tech/Labs/Other). Agents assigned by dept. Reply threads, status tracking.',
        backend: ['backend/app/routers/support.py', 'db: tickets, ticket_messages'],
        frontend: ['frontend/src/pages/HelpCenter.jsx', 'frontend/src/pages/TicketDetail.jsx'],
        apis: ['POST /api/support/tickets', 'GET /api/support/tickets/my', 'POST /api/support/tickets/{id}/reply'],
      },
      {
        name: 'Agent Dashboard',
        description: 'Agents see dept-filtered tickets, take/close/assign. Admin manages agents under Agents tab.',
        backend: ['backend/app/routers/support.py + admin.py (agent CRUD)'],
        frontend: ['frontend/src/pages/AgentDashboard.jsx', 'admin → Agents tab (AgentsTab.jsx)', 'admin → Tickets tab (TicketsTab.jsx)'],
        apis: ['GET /api/agent/tickets', 'POST /api/agent/tickets/{id}/take', '/close'],
      },
      {
        name: 'Secure File Attachments',
        description: 'Up to 2 PNG/JPG (2MB each) per reply from students. 5-layer security: extension/MIME/size/Pillow verify/EXIF strip. Agents can view only (no upload).',
        backend: ['backend/app/routers/support.py → upload_attachment, Pillow image verification'],
        frontend: ['frontend/src/pages/TicketDetail.jsx → file input'],
        apis: ['POST /api/support/tickets/{id}/attach'],
      },
    ],
  },

  // ─────────── 👨‍💼 ADMIN PANEL ───────────
  {
    category: 'admin',
    title: 'Admin Panel (21 Tabs)',
    icon: Users,
    color: 'orange',
    features: [
      {
        name: 'Users Tab',
        description: 'Paginated user list, search, enable/disable, view payments/enrollments, admin notes, and HARD-DELETE user (with full cascade cleanup).',
        backend: ['backend/app/routers/admin.py → /api/admin/users'],
        frontend: ['frontend/src/components/admin/UsersTab.jsx'],
        apis: ['GET /api/admin/users', 'POST /api/admin/users/{id}/disable', '/enable'],
      },
      {
        name: 'Hard-Delete User (Self-Service Cleanup)',
        description: 'Trash icon on every user row. Click → modal asks admin to retype the exact email of the target user (defence against UUID typos / wrong row). On confirm, the backend hard-deletes the user and CASCADES the cleanup across 10 collections so no orphan records survive: users, enrollments, payments, progress, lab_progress, course_completions, notifications, user_lab_attempts_versioned, user_notes, password_resets. Every deletion is best-effort wrapped (missing collection / schema variant won\'t block the rest). Emits a row in admin_audit_log {action: "delete_user", target_email, deletion_summary, timestamp}. Toast shows total records cleaned. Built so admin can spin up a fresh demo user, walk a client through the portal, then wipe it in 2 clicks — no more pinging the dev to delete from Mongo.',
        backend: [
          'backend/app/routers/admin.py → delete_user_completely() (after toggle-status route)',
          'safety: confirm_email query param must equal stored user.email (case-insensitive)',
          'audit: collection admin_audit_log',
        ],
        frontend: [
          'frontend/src/components/admin/UsersTab.jsx → openDeleteModal() + confirmDeleteUser() + delete confirmation Modal at end of component',
          'data-testids: delete-user-{id}, delete-user-modal, delete-user-confirm-input, delete-user-confirm, delete-user-cancel',
        ],
        apis: ['DELETE /api/admin/users/{user_id}?confirm_email=<exact-email>'],
      },
      {
        name: 'Payments Tab',
        description: 'All payment records. USD shown in ₹ equivalent + small USD. Friendly project names. Pagination (numbered buttons). CSV export.',
        backend: ['backend/app/routers/admin.py → /api/admin/payments (line 489-538)'],
        frontend: ['frontend/src/components/admin/PaymentsTab.jsx', 'adminUtils.js → formatAmount()'],
        apis: ['GET /api/admin/payments?page=N&limit=20'],
      },
      {
        name: 'Enroll Tab',
        description: 'Manual course/All Access grant to users. Revoke enrollments. Grant project access. History log.',
        backend: ['backend/app/routers/admin.py → /enroll-manual, /revoke-enrollment'],
        frontend: ['frontend/src/components/admin/EnrollTab.jsx'],
        apis: ['POST /api/admin/enroll-manual', 'POST /api/admin/revoke-enrollment'],
      },
      {
        name: 'Revenue Tab',
        description: 'Total revenue (period + all-time), course-wise breakdown, daily chart. USD normalized to INR via USD_TO_INR_RATE (90).',
        backend: ['backend/app/routers/admin.py → /revenue-stats', 'helper: _to_inr()'],
        frontend: ['frontend/src/components/admin/RevenueTab.jsx'],
        apis: ['GET /api/admin/revenue-stats?days=30'],
      },
      {
        name: 'Analytics Tab',
        description: 'User growth (daily new + cumulative), popular courses, revenue trend, full leaderboard.',
        backend: ['backend/app/routers/analytics.py → admin_router'],
        frontend: ['frontend/src/components/admin/AnalyticsTab.jsx (recharts)'],
        apis: ['GET /api/admin/analytics/popular-courses', '/user-growth', '/revenue-trend', '/leaderboard'],
      },
      {
        name: 'Courses Tab',
        description: 'Lists all 16 courses with lab counts. Read-only catalog view.',
        backend: ['GET /api/courses (uses specs_loader)'],
        frontend: ['frontend/src/components/admin/CoursesTab.jsx'],
        apis: ['GET /api/courses'],
      },
      {
        name: 'Server / Activity / Security / Network Tabs',
        description: 'System health, audit logs, security events, PPS network monitoring with live graph.',
        backend: ['backend/app/routers/admin.py, pps_monitoring.py'],
        frontend: ['ServerTab.jsx, ActivityTab.jsx, SecurityTab.jsx, NetworkTab.jsx'],
        apis: ['GET /api/admin/server-stats', '/audit-log', '/security-events', '/pps/status'],
      },
      {
        name: 'Tickets / Agents Tabs',
        description: 'Admin views all tickets, manages support agents (create, assign dept, enable/disable).',
        backend: ['backend/app/routers/admin.py → /agents endpoints'],
        frontend: ['TicketsTab.jsx, AgentsTab.jsx'],
        apis: ['GET /api/admin/tickets', 'POST /api/admin/agents'],
      },
      {
        name: 'CI/CD Deploy Pipeline — Smart Dependency Check (May 2026)',
        description: '🚀 GitHub Actions workflow on push to release/svradm-v1 → SSH into prod → git pull → smart pip install → frontend build → backend restart. Originally `pip install -r requirements.txt` ran on EVERY deploy taking 14 min even when no Python deps changed (heavy packages: pandas, numpy, boto3, litellm, google-genai etc.). Worse: a deploy once hung 49 minutes silently because the --quiet flag hid the actual problem (PyPI/CloudFront network timeout).\n\n✨ Smart fix (.github/workflows/deploy.yml):\n  • Detect whether backend/requirements.txt actually changed using `git diff HEAD@{1} HEAD --name-only`\n  • If unchanged → skip pip install entirely (saves ~13 min on most deploys)\n  • If changed → run with `--timeout 60 --retries 3 --extra-index-url https://d33sy5i8bnduwe.cloudfront.net/simple/`. The CloudFront index is required for the `emergentintegrations` package (not on regular PyPI); --quiet removed so progress is visible in GitHub Actions logs.\n\n⏱️ Result: code-only deploy 30–45 sec, dependency-change deploy 5–8 min with timeout protection, hung deploys now fail clean instead of dragging on for an hour.\n\n🐛 Companion fix: `httpx` was missing from prod venv on first deploy after the anti-bot stack landed → backend crashed on startup with ModuleNotFoundError. Resolution: ensured requirements.txt contains httpx==0.28.1 (it always did — root cause was the pipeline never running pip install). The smart pip-install step now guarantees this can never recur because new deps trigger an install automatically.',
        backend: ['backend/requirements.txt → httpx==0.28.1, emergentintegrations==0.1.0', '.github/workflows/deploy.yml → smart git-diff check + --extra-index-url + --timeout 60 --retries 3'],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        name: 'Microsoft Clarity — Session Recordings + Heatmaps (May 2026)',
        description: '🎥 Installed Microsoft Clarity (free, unlimited) — gives literal VIDEO REPLAY of every user session: mouse movement, clicks, scroll depth, rage clicks, dead clicks, JS errors, mobile vs desktop behaviour. Project ID: `wpvurp3g6h`. Loaded asynchronously from the <head> of `index.html` via the official snippet so it never blocks page render.\n\n🛣️ SPA-friendly route tracking: Clarity\'s base snippet fires ONE page-view on initial load — every subsequent React Router navigation is invisible to Clarity by default. Solved via the custom `useClarityTracking()` hook (frontend/src/hooks/useClarityTracking.js) which listens to `useLocation()` and calls `clarity("set", "page", path)` on every route change. Mounted inside `<AppRoutes>` in App.js so it runs for every authenticated AND unauthenticated visit.\n\n🆔 User identification (cross-session linking): on login, restored-session, and post-registration we call `identifyClarityUser(email, traits)` which SHA-1 hashes the email client-side (never raw PII reaches Clarity) and calls `clarity("identify", hashedId)`. This means all of a user\'s past + future sessions are linked into one timeline in the Clarity dashboard — admin can pick "ravi.sarswat@gmail.com" and watch his entire journey across days/devices.\n\n🏷️ Session tagging: `tagClaritySession(key, value)` adds dimensions for filtering. We currently tag `event=signup` on registration and `event=login` on login. Future tags can include `plan=all_access`, `source=meta_ad`, `user_type=student` for advanced cohort filtering.\n\n💡 Use case (real example expected within 24-48 hours of deploy): pick any Meta-ad signup\'s session → watch the recording → identify exactly where they abandoned (pricing scary? mobile UI broken? form field confusing?) → fix targeted issue instead of guessing. This is the FIRST tool that gives the admin direct visibility into post-signup user behaviour.',
        backend: ['—'],
        frontend: [
          'frontend/public/index.html → Clarity snippet in <head> (project: wpvurp3g6h)',
          'frontend/src/hooks/useClarityTracking.js → useClarityTracking hook + identifyClarityUser + tagClaritySession exports',
          'frontend/src/App.js → useClarityTracking() mounted inside <AppRoutes> for SPA route tracking',
          'frontend/src/context/AuthContext.jsx → identify on login + restored session + post-register (with event=signup / event=login tag)',
        ],
        apis: ['— (client-side only)'],
      },
      {
        name: 'Global tz_aware UTC Migration + Off-By-One-Day Fixes (May 2026)',
        description: '🕒 Cascading multi-day fix triggered by user noticing dates showing "10 May" for events that happened on "11 May 00:32 IST". Single config change with deep downstream impact.\n\n🩹 Layer 1 — Display fix: `backend/app/db.py` AsyncIOMotorClient constructed with `tz_aware=True`. Without it, Motor returned naive datetimes (UTC values without tzinfo) → Pydantic isoformat() emitted "2026-05-10T19:02:48" with no `+00:00` marker → frontend `new Date()` parsed as LOCAL time → Users/Payments/Email Activity tabs all showed off-by-one-day. After fix every datetime field gets `tzinfo=UTC` automatically → ISO output `"2026-05-10T19:02:48+00:00"` → frontend renders correct IST.\n\n🔧 Layer 2 — Type-safety migration: `tz_aware=True` made all Mongo reads aware, but the codebase had 69 places using `datetime.utcnow()` (NAIVE) for comparisons with Mongo-read aware datetimes → instant TypeError: "can\'t compare offset-naive and offset-aware datetimes". Global sed-style replace: `datetime.utcnow()` → `datetime.now(timezone.utc)` across 12 files (admin.py 28x, payments.py 12x, vm_session_manager.py 10x, auth.py 5x, projects.py 2x, verify.py 2x, security.py 2x, access_control.py 2x, email_templates.py 2x, main.py 2x, lab.py 1x, support.py 1x) + added `timezone` import where missing. Same UTC moment, but now tz-aware, safe to compare with Mongo values.\n\n🐛 Layer 3 — Cascading bug discoveries surfaced by Layer 1+2:\n  (a) `/api/admin/grant-history` 500 crash → fixed (was comparing naive utcnow with aware Mongo dates).\n  (b) `/api/admin/users/{id}/progress` 500 → admin saw zero progress for users like Manu (80 labs / 6565 score in DB) → root cause was `sorted(..., key=lambda x: x.get("last_activity") or "", reverse=True)` mixing datetime with empty string. Fix: defensive sort key using `_EPOCH = datetime(1970, 1, 1, tzinfo=timezone.utc)` fallback.\n  (c) `/api/progress/dashboard/summary` 500 → 16 student dashboards showed zero progress. Root cause: 150 LEGACY progress rows had `last_activity` as STRING (no tz marker) while newer rows had tz-aware datetime → mixed sort crashed. Two-layer fix: (i) defensive sort in both `/my` and `/dashboard/summary` handlers that parses strings as ISO and force-attaches UTC tzinfo, (ii) one-shot migration `backend/scripts/migrate_string_last_activity.py` that converted all 150 string rows → BSON Date. Idempotent — re-runnable safely; remaining string rows after migration = 0.\n\n🔍 Layer 4 — Frontend display polish: `frontend/src/components/admin/adminUtils.js` `formatDate()` switched from `toLocaleDateString()` (silently ignored hour/minute options) → `toLocaleString()` so time-of-day actually renders ("11 May 2026, 10:15 am" instead of just "11 May 2026").\n\n🛡️ Audit confirmed (16 critical collections × 28 datetime fields): all enrollments, project_purchases, payments, password_resets, vm_sessions, users, notifications, announcements, email_log, registration_attempts, support_tickets, progress fields are uniformly tz-aware after migration. Tested expired enrollment access (correctly BLOCKED) + active enrollment access (correctly ALLOWED) on real prod data — zero free-access leaks from the tz change.',
        backend: [
          'backend/app/db.py → AsyncIOMotorClient(..., tz_aware=True)',
          'backend/app/routers/{admin,payments,auth,lab,projects,verify,support}.py + security.py + services/access_control.py + utils/email_templates.py + vm_session_manager.py + main.py → 69 occurrences of datetime.utcnow() replaced with datetime.now(timezone.utc)',
          'backend/app/routers/admin.py line 1811 → defensive _lab_sort_key using _EPOCH fallback',
          'backend/app/routers/progress.py → defensive get_sort_key in /my and /dashboard/summary handlers (handles datetime, naive, string, None gracefully)',
          'backend/scripts/migrate_string_last_activity.py → idempotent one-shot migration (150 rows converted, 0 remaining)',
        ],
        frontend: [
          'frontend/src/components/admin/adminUtils.js → formatDate uses toLocaleString (with hour:2-digit minute:2-digit) instead of toLocaleDateString',
        ],
        apis: ['— (fix is internal; no API surface change)'],
      },
      {
        name: 'Projects / Announce Tabs',
        description: 'CRUD real-world projects (section editor, toggle free/paid). Announce: platform-wide banner message.',
        backend: ['backend/app/routers/projects.py (admin endpoints)', 'backend/app/routers/admin.py → /announce'],
        frontend: ['ProjectsTab.jsx, AnnouncementTab.jsx'],
        apis: ['POST /api/admin/projects', 'POST /api/admin/announce'],
      },
      {
        name: 'New Course Guide Tab (May 16, 2026)',
        description: 'Read-only admin reference checklist for adding a new course to the platform. 16 steps across 8 layers (Frontend Constants, Backend Pricing, Frontend Pages, Backend Course Ordering, Access Control, YAML Lab Specs, Pytest Tests, Admin Docs). Each step has a priority badge (CRITICAL/HIGH/OPTIONAL), exact file path, section reference, plain-English description, and a styled code example. Session-scoped progress tracker — admin can tick steps as they go. Two pinned rule banners: ALL_ACCESS_EXCLUDED sync rule and the silent-vs-notifying YAML edit rule. Pre-launch validation checklist with 11 items at the bottom.',
        backend: ['—'],
        frontend: [
          'frontend/src/components/admin/NewCourseGuideTab.jsx (self-contained, read-only)',
          'frontend/src/pages/AdminDashboard.jsx → import + tab entry (BookOpen icon)',
        ],
        apis: ['—'],
      },
      {
        name: 'Health Monitor Tab (May 16, 2026)',
        description: '19 API endpoint checks across 5 categories (Platform, Auth, Payment, Access, Admin), auto-runs every 15 minutes with a live countdown timer and manual "Run Checks Now" button. Test user credentials entered via email/password fields (stored in component state only, never persisted). Checks are phased: Phase 1 — public endpoints in parallel; Phase 2 — login with test user credentials to obtain JWT; Phase 3 — JWT-authenticated endpoints in parallel; Phase 4 — admin endpoints using current admin session token. Each check row shows: status (✅ PASS / ❌ FAIL / ⏳ CHECKING / — SKIP), response time in ms (colour-coded: green <800ms, amber <2s, red >2s), and last-checked timestamp. Red alert banner lists all failing checks by name. Skipped checks shown with reason (no credentials / login failed / no admin token). Response time thresholds: green < 800ms, amber < 2s, red ≥ 2s.',
        backend: ['—'],
        frontend: [
          'frontend/src/components/admin/HealthMonitorTab.jsx (self-contained, no backend changes)',
          'frontend/src/pages/AdminDashboard.jsx → import + tab entry (Activity icon)',
        ],
        apis: [
          'GET /api/payments/all-access (platform)',
          'GET /health → replaced by GET /api/admin/stats (DB connected check)',
          'GET /api/payments/course-pricing/DOCKER (platform)',
          'POST /api/auth/login (auth)',
          'GET /api/auth/verify (auth)',
          'POST /api/auth/check-email (auth)',
          'GET /api/payments/all-access-pricing (payment)',
          'GET /api/payments/my-payments (payment)',
          'GET /api/courses (access)',
          'GET /api/lab/list/RH124 (access)',
          'GET /api/progress/my (access)',
          'GET /api/progress/dashboard/summary (access)',
          'GET /api/leaderboard/top (access)',
          'GET /api/admin/verify (admin)',
          'GET /api/admin/stats (admin)',
          'GET /api/admin/email-logs/stats (admin)',
          'GET /api/admin/pps/current (admin)',
          'GET /api/admin/vm-audit (admin)',
        ],
      },
    ],
  },

  // ─────────── 🔔 NOTIFICATIONS & LAB CHANGE DETECTION ───────────
  {
    category: 'notifications',
    title: 'Notifications & Lab Change Detection',
    icon: BookOpen,
    color: 'blue',
    features: [
      {
        name: 'Unified Notification System',
        description: 'In-app notifications inbox per user. Severity levels: info / warning / critical. Categories: lab_update, announcement, batch_news, job_alert, system. Floating FAB bell at bottom-right (App.js global, NOT inside navbar — broke layout last time, kept outside permanently). Live polling every 60s for unread count. Click bell → dropdown preview; click "View all" → /notifications page with filter chips (All / Unread / Labs / Announcements / Batches / Jobs).',
        backend: [
          'backend/app/routers/notifications.py (user + admin routers)',
          'collection: notifications  → {user_id, type, severity, title, message, metadata, is_read, requires_action, created_at}',
        ],
        frontend: [
          'frontend/src/components/NotificationBell.jsx (FAB + dropdown)',
          'frontend/src/pages/Notifications.jsx (full inbox page)',
          'rendered globally inside App.js (NOT navbar)',
        ],
        apis: [
          'GET /api/notifications/my',
          'GET /api/notifications/unread-count',
          'POST /api/notifications/{id}/read',
          'POST /api/notifications/mark-all-read',
          'DELETE /api/notifications/{id}',
        ],
      },
      {
        name: 'Admin Manual Broadcast',
        description: 'Admin can send announcements / batch news / job alerts to targeted audiences: All users, course-enrolled, no-enrollment (cold leads), or specific users (paste user IDs OR emails — both supported). Severity tagging. Action link optional.',
        backend: [
          'backend/app/routers/notifications.py → admin_router /broadcast',
          'audience resolver supports email lookup (not just UUID)',
        ],
        frontend: ['frontend/src/components/admin/BroadcastTab.jsx'],
        apis: ['POST /api/admin/notifications/broadcast'],
      },
      {
        name: 'Auto Lab Change Detection (YAML diff)',
        description: 'Background detector that runs ON BACKEND STARTUP (NOT real-time on YAML save — file watcher intentionally disabled). Reads all course/lab YAML files via specs_loader, computes structural checksum (id + title + points only — hints/descriptions ignored). Compares with db.lab_versions baseline → emits granular diff: added / modified / removed task lists. On change: bumps lab version (v1 → v2 → v3...), persists to db.lab_versions + db.lab_change_log, recalculates affected user scores, sends bulk notifications to enrolled + lab-attempters. Bootstrap mode: first-ever run silently saves baseline (no false notifications). Idempotent: same checksum twice → no duplicate notification.',
        backend: [
          'backend/app/services/lab_change_detector.py (~563 lines)',
          'collection: lab_versions       → {course_id, lab_id, version, checksum_structural, tasks_snapshot}',
          'collection: lab_change_log     → {course_id, lab_id, from_version, to_version, change_type, details, notified_users_count, scores_recalculated_for_users, detected_at}',
          'collection: system_config      → {lab_change_detector: {bootstrap_completed: true}}',
          'invocation: backend/app/main.py startup → asyncio.create_task(detect_and_notify_lab_changes(db))',
        ],
        frontend: ['—'],
        apis: ['GET /api/admin/lab-changes (returns full diff details for drill-down modal)'],
      },
      {
        name: 'Score Recalculation Logic (per task change type)',
        description: '🎯 SIMPLE RULE: Sirf jo task badla aur user ne pehle complete kiya tha — wahi invalidate hota hai. Baaki untouched tasks safe rehte hain. Pure wipe out kabhi NAHI hota.\n\n📋 5 cases:\n  • Task ADDED        → notify only. Existing completed_tasks UNTOUCHED. New task starts at 0 pts for all.\n  • Task TITLE CHANGED → notify + INVALIDATE that task from completed_tasks (force re-attempt). Score recomputed.\n  • Task POINTS-ONLY changed (title same) → notify + KEEP completion. Score auto-recomputed with new weight (e.g. q1 was 5 pts → 10 pts → user instantly gets +5 without re-attempting).\n  • Task REMOVED      → notify + drop task from completed_tasks. Score recomputed (those points gone).\n  • Hint / description / setup edited (title + points unchanged) → SILENT. No notification, no score change. Detector ignores by design (line 51-58: only id+title+points hashed).\n\n💡 Practical tip: typo fix karna ho without disrupting students → edit hint, NOT title. Concept hi badle to title change karo (fairly notify + re-attempt).',
        backend: [
          'backend/app/services/lab_change_detector.py → recalc_user_scores_for_lab() (line 128-200)',
          'invalidation logic: line 156-164 — only TITLE diff triggers invalidate; pure points change preserves',
          'score recompute: line 178-182 — sum of specs_loader.get_task_points() for each remaining completed_task',
          'mutation: db.progress.update_one({user_id, course_id, lab_id}, {$set: {completed_tasks, score, last_activity}})',
        ],
        frontend: [
          'student sees: warning + "ACTION NEEDED" badge → Notifications page',
          'admin sees: BroadcastTab → click any auto-detected change → diff modal with old/new title side-by-side',
        ],
        apis: ['—'],
      },
      {
        name: 'Lab Change Drill-Down Modal (Admin)',
        description: 'Click any auto-detected lab change card in Broadcast tab → modal shows full diff: added tasks (green), modified tasks (amber, old/new title side-by-side with strikethrough, points change with arrow), removed tasks (rose). Contextual labels: "Points-only change — student progress preserved" or "Title changed → re-attempt required". Header shows version bump (v1→v2), notified count, scores recalculated count. Esc + backdrop click + 2 close buttons all work.',
        backend: ['backend/app/routers/notifications.py → admin_lab_changes_log() returns added_tasks/modified_tasks/removed_tasks arrays with old vs new titles'],
        frontend: ['frontend/src/components/admin/BroadcastTab.jsx → <LabChangeDetailModal /> (bottom of file)'],
        apis: ['GET /api/admin/lab-changes (extended response)'],
      },
      {
        name: 'Audience Targeting',
        description: 'For lab change notifications: ALL users enrolled in the course + ANY user with progress on that specific lab (even if not enrolled — covers free-trial attempts). Filter: is_active != false. For broadcasts: admin chooses (all / course_enrolled / no_enrollment / specific_users by ID or email).',
        backend: ['lab_change_detector.py → resolve_target_users() (~line 220+)'],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        name: 'Notification Categories — Visual Mapping',
        description: 'Each notification type has icon + color + bg in Notifications.jsx (TYPE_META). lab_update: BookOpen + blue, announcement: Megaphone + amber, batch_news: GraduationCap + emerald, job_alert: Briefcase + purple, system: Bell + slate. Severity adds extra badge: warning → amber "Important", critical → rose "Critical". requires_action=true adds "Action needed" amber badge.',
        backend: ['—'],
        frontend: ['frontend/src/pages/Notifications.jsx → TYPE_META (line 11)'],
        apis: ['—'],
      },
      {
        name: 'Bulletproof Email Delivery Monitoring (3 Layers)',
        description: '📨 Designed after the May 2026 silent SMTP outage (Microsoft Security Defaults blocked basic auth for ~24h with zero alerts).\n\n🔒 LAYER 1 — DB Audit Logging: every send_email() call now records a row in db.email_log {recipient, subject, template, triggered_by, status (sent/failed/simulated), smtp_response, duration_ms, sent_at}. Best-effort logger never raises so a Mongo blip cannot break a send.\n\n📊 LAYER 2 — Admin "Email Activity" Tab: filterable + paginated audit table, 6 stat cards (Sent / Failed / Simulated / Last 24h / Last failure / Last canary), success-rate %, health badge (Healthy / Degraded / Down), banner alerts when failure-rate > 25% or zero sends in 24h, "Resend" button for failed welcome emails.\n\n🐤 LAYER 3 — SMTP Canary Cron: backend/scripts/smtp_canary.py + grras-smtp-canary.timer (every 6h) sends a tiny "I\'m alive" email to support@grras.tech (configurable via CANARY_RECIPIENT). Logged with triggered_by=\'canary\' so the dashboard shows the freshest probe + its latency.\n\n🚨 Verdict logic: down = 0 sent + N failed in last 24h, degraded = >25% failure rate, healthy = otherwise. If ever Microsoft re-enables Security Defaults silently, the dashboard flips RED inside 6h and admin sees the EXACT smtp_response error — no more guessing.',
        backend: [
          'backend/app/services/email_service.py → _log_email_attempt() (line 73), send_canary_email() (bottom)',
          'backend/app/routers/admin.py → /email-logs, /email-logs/stats, /email-logs/canary, /email-logs/resend',
          'collection: email_log → {recipient, subject, template, triggered_by, status, smtp_response, duration_ms, sent_at}',
          'backend/scripts/smtp_canary.py + grras-smtp-canary.{service,timer} (deploy to /etc/systemd/system on prod)',
        ],
        frontend: ['frontend/src/components/admin/EmailActivityTab.jsx (registered in AdminDashboard)'],
        apis: [
          'GET /api/admin/email-logs?page=&limit=&status_filter=&recipient=&days=',
          'GET /api/admin/email-logs/stats?days=',
          'POST /api/admin/email-logs/canary',
          'POST /api/admin/email-logs/resend',
        ],
      },
      {
        name: 'Email Activity — triggered_by Filter (May 17, 2026)',
        description: 'Dropdown filter added to the Email Activity admin tab so logs can be filtered by the trigger that sent each email. Original options: All triggers, Registration, Admin Notification, Payment Confirmation, Course Enrollment, Password Reset, Abandoned Cart, Payment Notification, Canary, Admin Resend. Updated May 19, 2026 to include 7 re-engagement options under a ── Re-engagement ── separator: Drip D1 (Free Labs), Drip D3 (Getting Started), Drip D7 (Real Servers), Drip D14 (RHCSA Cert), Drip D30 (Last Email), Buyer D1 (Start Lab), Buyer D3 (Keep Going). Passes triggered_by as a query param to the backend; page resets to 1 on filter change.',
        backend: [
          'backend/app/routers/admin.py → GET /api/admin/email-logs?triggered_by= (was already implemented)',
        ],
        frontend: [
          'frontend/src/components/admin/EmailActivityTab.jsx → triggeredBy state (useState) + params.set("triggered_by", triggeredBy) in fetchLogs + dropdown in Filters card',
        ],
        apis: ['GET /api/admin/email-logs?triggered_by=<value>'],
      },
      {
        name: 'Global UTC Timezone Fix (Off-by-one-day bug)',
        description: '🕒 Found that Users tab + Payments tab + Email Activity tab were ALL showing "10 May" for events that happened on "11 May 00:32 IST" — classic off-by-one-day bug.\n\n🐛 Root cause: Motor returned naive datetime objects (UTC values without tzinfo). When Pydantic serialised them via isoformat() the output was "2026-05-10T19:02:48" — NO `+00:00` marker. Frontend `new Date(...)` then parsed the string as LOCAL time → 19:02 local on 10 May instead of 19:02 UTC on 10 May = 00:32 IST on 11 May. One missing 6-character UTC marker affected EVERY date displayed in the admin panel.\n\n🩹 Fix (1-line, global): in backend/app/db.py the AsyncIOMotorClient is constructed with `tz_aware=True`. Now Motor attaches `tzinfo=UTC` to every datetime it reads from MongoDB → Pydantic\'s isoformat() emits `"2026-05-10T19:02:48+00:00"` → frontend JS Date() correctly parses as UTC → toLocaleString("en-IN", timeZone: "Asia/Kolkata") shows the correct IST timestamp on every page.\n\n✨ Bonus fix: frontend/src/components/admin/adminUtils.js → formatDate() was using toLocaleDateString() which silently ignored the hour/minute options. Switched to toLocaleString() so time-of-day actually renders (e.g. "11 May 2026, 10:15 am" instead of just "11 May 2026").\n\n📍 Affected surfaces (all auto-fixed by the single tz_aware=True flag): Users tab created_at + last_login, Payments tab created_at, Enrollments granted_at + expires_at, Email Activity sent_at, Lab Sessions created_at, Support Tickets, Activity Logs, anything reading a datetime from MongoDB. Pre-existing email_log _utc_iso helper kept for safety but now redundant.',
        backend: [
          'backend/app/db.py → AsyncIOMotorClient(..., tz_aware=True)  (1-line global fix)',
          'backend/app/routers/admin.py → _utc_iso helper (defensive duplicate, harmless after tz_aware=True)',
        ],
        frontend: [
          'frontend/src/components/admin/adminUtils.js → formatDate() now uses toLocaleString (not toLocaleDateString)',
        ],
        apis: ['— (fix is global, no API surface change)'],
      },
    ],
  },

  // ─────────── 🔌 INTEGRATIONS ───────────
  {
    category: 'integrations',
    title: '3rd Party Integrations',
    icon: Zap,
    color: 'cyan',
    features: [
      {
        name: 'Razorpay (Payments)',
        description: 'Live keys: rzp_live_RNsLD... Live mode active. INR + USD currency support enabled on account.',
        backend: ['env: RAZORPAY_KEY_ID, RAZORPAY_KEY_SECRET'],
        frontend: ['frontend/src/pages/Payment.jsx → loadRazorpayScript()'],
        apis: ['external: checkout.razorpay.com/v1/checkout.js'],
      },
      {
        name: 'GPT-5-nano (via Emergent LLM Key)',
        description: 'AI caption generation for Achievement Posters. 3 tone variants (formal/celebratory/inspirational).',
        backend: ['backend/app/routers/admin.py → caption endpoint', 'pkg: emergentintegrations'],
        frontend: ['AchievementPosterTab.jsx → ✨ AI Suggest button'],
        apis: ['POST /api/admin/poster/suggest-captions'],
      },
      {
        name: 'MongoDB Atlas',
        description: 'Primary DB (labplatform_prod). Collections: users, enrollments, payments, progress, mock_test_attempts, tickets, projects, vm_sessions, vm_audit_log, admin_user_notes.',
        backend: ['backend/app/db.py', 'env: MONGO_URL, MONGO_DB_NAME'],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        name: 'ip-api.com / ipapi.co (Geolocation)',
        description: 'Detect user country for INR vs USD display. Fallback to India if both fail.',
        backend: ['—'],
        frontend: ['frontend/src/hooks/useCurrency.js'],
        apis: ['external geolocation'],
      },
    ],
  },

  // ─────────── 📧 EMAIL MARKETING & RE-ENGAGEMENT ───────────
  {
    category: 'email_marketing',
    title: 'Email Marketing & Re-engagement',
    icon: Mail,
    color: 'violet',
    features: [
      {
        name: 'Re-engagement Drip System (D1–D30)',
        description: 'Automated 5-email drip sequence for new users who have not purchased. Background loop runs every hour (10-min startup warm-up). Anti-spam guards enforced before every send: 48h minimum gap between emails, max 2 per 7-day rolling window, skip if user logged in within 24h.\n\n📧 D1 (24–72h after signup) — "free labs are ready" angle. Window capped at 72h so the "yesterday" framing stays accurate — users registered 7+ days ago get drip_d1_sent_at stamped "skipped" and fall through to D3/D7/D14/D30 on the same pass.\n\n📧 D3 (72h, no login since signup) — quick-start help + direct support offer.\n\n📧 D7 (7 days, no payment) — social proof banner with total_students count, real VM vs simulator comparison table.\n\n📧 D14 (14 days, no payment) — RHCSA career angle, certification value paragraph (no hardcoded salary figures — removed after review).\n\n📧 D30 (30 days, no payment) — respectful final email, no hard sell. Account stays open. No more emails after this.',
        backend: [
          'backend/app/services/reengagement.py → run_reengagement() + reengagement_loop()',
          'backend/app/services/engagement_config.py → ANTI_SPAM dict',
          'backend/app/main.py → app.state.reengagement_task = asyncio.create_task(reengagement_loop())',
          'backend/app/services/email_service.py → send_drip_d1/d3/d7/d14/d30_email()',
          'dedup fields on db.users: drip_d1_sent_at, drip_d3_sent_at, drip_d7_sent_at, drip_d14_sent_at, drip_d30_sent_at (value "skipped" for expired D1 window)',
          'anti-spam fields on db.users: last_reengagement_sent_at, reengagement_sent_count_week, reengagement_week_start',
        ],
        frontend: ['—'],
        apis: ['— (internal background loop)'],
      },
      {
        name: 'Retention Email Loop (Streak Risk · Almost Done · Weekly Digest)',
        description: 'Behavioural trigger emails for active users. Background loop runs every hour (15-min startup warm-up). Anti-spam: 48h min gap, max 2 emails per 7-day rolling window, skip if active within 2h.\n\n🔥 streak_risk — user has streak >= 2 days but no lab activity today. Sends once per day max. Shows streak count, what they lose if it breaks, last incomplete lab as resume link.\n\n📌 almost_done — lab is 60%+ complete but untouched for 3+ days. Sends once per lab per user. Shows progress bar, tasks remaining, XP reward on completion.\n\n📅 weekly_digest — Sunday 7 PM IST (13:00 UTC) only. User must have had activity in last 7 days. Shows labs done, XP earned, streak, rank, next suggested lab.',
        backend: [
          'backend/app/services/retention.py → run_retention() + retention_loop()',
          'backend/app/main.py → app.state.retention_task = asyncio.create_task(retention_loop())',
          'backend/app/services/email_service.py → send_streak_risk_email() / send_almost_done_email() / send_weekly_digest_email()',
          'backend/app/templates/emails/streak_risk.html / almost_done.html / weekly_digest.html',
          'dedup fields on db.users: streak_risk_sent_date, almost_done_sent_labs (array), weekly_digest_sent_at',
          'anti-spam fields: last_retention_sent_at, retention_sent_count_week, retention_week_start',
          'admin test: POST /api/admin/retention/test-email {email, email_type}',
        ],
        frontend: [
          'frontend/src/components/admin/EmailActivityTab.jsx → Retention section in triggered_by filter + Test Retention Email panel',
        ],
        apis: ['POST /api/admin/retention/test-email (super_admin only)'],
      },
      {
        name: 'Win-back Email Sequence (D7 → D45)',
        description: 'Re-engagement sequence for users who completed 1+ lab but have gone inactive. Background loop runs every hour (20-min warm-up). Each stage sends exactly once per user. After D45, user flagged winback_suppressed=True.\n\nD7 — soft nudge, progress snapshot (labs + XP), resume link.\nD14 — course-aware (uses user most-active course: Python/Docker/Ansible etc.), industry value content.\nD21 — social proof: live count of engineers active this week (from DB, floor=10).\nD30 — loss aversion: momentum fading, shows user stats.\nD45 — breakup email: Should we stop emailing you? After send winback_suppressed=True.\n\nUsers with 0 completed labs excluded (reengagement drip handles them instead).',
        backend: [
          'backend/app/services/winback.py → run_winback() + winback_loop()',
          'backend/app/main.py → app.state.winback_task = asyncio.create_task(winback_loop())',
          'backend/app/services/email_service.py → send_winback_d7/d14/d21/d30/d45_email()',
          'backend/app/templates/emails/winback_d7/d14/d21/d30/d45.html',
          'dedup fields on db.users: winback_d7_sent_at … winback_d45_sent_at, winback_suppressed',
          'anti-spam fields: last_winback_sent_at, winback_sent_count_week, winback_week_start',
          'admin test: POST /api/admin/winback/test-email {email, email_type}',
        ],
        frontend: [
          'frontend/src/components/admin/EmailActivityTab.jsx → Win-back section in triggered_by filter + Test Win-back Email panel',
        ],
        apis: ['POST /api/admin/winback/test-email (super_admin only)'],
      },
      {
        name: 'Buyer Activation Emails (D1 + D3 post-purchase)',
        description: '2-email sequence for users who purchased a course but have not started a lab.\n\n📧 buyer_d1 (same day as purchase) — "you\'re in" angle with 3-step start guide (login → open course → Start Lab). Lists what is now unlocked: all labs, auto-verified tasks, real RHEL VMs.\n\n📧 buyer_d3 (3 days after purchase) — low-commitment framing: 10 min (5 commands), 20 min (one task), 45 min (full session). Reassurance that VMs save state between sessions. Direct support offer via support_email.',
        backend: [
          'backend/app/services/reengagement.py → (buyer drip hooks, called from future upsell engine)',
          'backend/app/services/email_service.py → send_buyer_d1_email(user_email, user_name, course_name)',
          'backend/app/services/email_service.py → send_buyer_d3_email(user_email, user_name, course_name)',
        ],
        frontend: ['—'],
        apis: ['— (internal, triggered post-purchase)'],
      },
      {
        name: 'Engagement Config — Career Path Engine',
        description: 'Single source of truth for course progression and email personalisation. Three dicts in one file — add one entry to add a new course and everything downstream (next-course upsell, exam readiness, mock-test nudge, cross-sell) picks it up automatically.\n\nCAREER_NEXT — maps each course_id to: next (natural next course in path), mock (mock test category), exam (certification exam, if applicable), cross (complementary skills). 13 courses mapped.\n\nCOURSE_NAMES — human-readable display names for all course IDs and exam IDs. Used in email subject lines and template variables.\n\nANTI_SPAM — guardrails applied before every re-engagement send: min_gap_hours=48, max_per_week=2, skip_if_active_hours=24, max_drip_emails=5, max_upsell_per_course=1, max_allaccess_upsell=2.',
        backend: [
          'backend/app/services/engagement_config.py → CAREER_NEXT, COURSE_NAMES, ANTI_SPAM',
          'Courses mapped: RH124 → RH134 → SVRADM → ANSIBLE → DOCKER → KUBERNETES → TERRAFORM',
          'Also: GIT, PYTHON, C_LANG, CPP, MYSQL, JAVA',
          'Exam IDs: EXAM_RHCSA, EXAM_EX294, EXAM_DO188',
        ],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        name: 'Email Templates — 11 Jinja2 HTML Files',
        description: 'All re-engagement and buyer emails use table-based Jinja2 HTML templates matching the payment_confirmation.html style: orange header (#EA580C), 600px max-width card, border-radius:8px, Arial font stack, {% include \'_footer.html\' %}.\n\nDrip templates: drip_d1.html (vars: user_name, dashboard_url), drip_d3.html (+ support_email), drip_d7.html (+ total_students), drip_d14.html, drip_d30.html.\n\nBuyer templates: buyer_d1.html (vars: user_name, course_name, dashboard_url), buyer_d3.html (+ support_email).\n\nUpsell templates: next_course.html (completed_course, next_course, next_course_name), exam_ready.html (completed_courses_list loop, exam_name, exam_code), mock_test.html (course_name, mock_course_id), allaccess_upsell.html (courses_bought, total_spent_display).',
        backend: [
          'backend/app/templates/emails/drip_d1.html → drip_d3.html → drip_d7.html → drip_d14.html → drip_d30.html',
          'backend/app/templates/emails/buyer_d1.html → buyer_d3.html',
          'backend/app/templates/emails/next_course.html → exam_ready.html → mock_test.html → allaccess_upsell.html',
        ],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        name: 'Email Currency Display Fix — total_spent_display',
        description: 'allaccess_upsell.html previously hardcoded ₹{{ total_spent_inr }} — broken for international USD users. Fixed: template now uses {{ total_spent_display }} with no currency prefix. The Python caller formats the symbol before passing to the template context.\n\nFormat rules: INR → f"₹{amount:,}" (with comma thousands separator), USD → f"${amount}", fallback → str(amount). The variable rename also applies to the <title> tag and the comparison table row.',
        backend: [
          'backend/app/templates/emails/allaccess_upsell.html → total_spent_display (3 occurrences — title, body, table)',
          'backend/app/services/email_service.py → TODO comment at line ~1167: format total_spent_display before passing to template',
        ],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        date: 'May 19, 2026',
        name: 'Daily Summary Email',
        description: 'Daily digest sent to admin@grras.tech at 23:00 IST (17:30 UTC) every night. Covers: new signups, payments (INR + USD), drip emails sent/failed, success rate, email health label (healthy / degraded / critical).\n\nBackground loop (daily_summary_loop) sleeps until 17:30 UTC each day, fires, then reschedules for the next day — same asyncio.sleep() pattern as cart_recovery_loop and reengagement_loop. Registered as app.state.daily_summary_task in main.py.\n\nDate range: IST midnight → midnight for the previous calendar day, converted to UTC boundaries before querying MongoDB.\n\nEmail is inline f-string HTML (not Jinja2) — same pattern as send_payment_notification_to_admin(). triggered_by = "daily_summary". Admin email resolved from ADMIN_NOTIFICATION_EMAIL env var (falls back to email_user).',
        backend: [
          'backend/app/services/daily_summary.py → daily_summary_loop(), run_daily_summary(), _get_summary_data(), _send_daily_summary()',
          'backend/app/main.py → app.state.daily_summary_task = asyncio.create_task(daily_summary_loop())',
        ],
        frontend: ['—'],
        apis: ['—'],
      },
      {
        date: 'May 19, 2026',
        name: 'Unsubscribe System',
        description: 'HMAC-signed one-click unsubscribe for all drip emails. Token = first 16 hex chars of HMAC-SHA256(jwt_secret, user_id) — constant-time comparison prevents timing attacks.\n\nFlow: user clicks link in email footer → GET /api/unsubscribe?uid=&token= → token verified → db.users $set {email_unsubscribed: true, unsubscribed_at: now} → audit row inserted into email_log (triggered_by="unsubscribe") → redirect to /unsubscribed confirmation page.\n\nReengagement loop skips users where email_unsubscribed=True before any drip is evaluated. generate_unsubscribe_url(user_id) is a module-level helper in email_service.py — imported in reengagement.py and called per-user.\n\nAll 5 drip send functions (send_drip_d1_email … send_drip_d30_email) now accept *, unsubscribe_url: str = None as a keyword-only arg and pass it into the Jinja2 template context. _footer.html renders the link only when the variable is defined and truthy ({% if unsubscribe_url is defined and unsubscribe_url %}).\n\nGET /api/unsubscribe/status?uid= returns {unsubscribed: true/false} for admin lookups.',
        backend: [
          'backend/app/routers/unsubscribe.py → GET /api/unsubscribe (verify token, set field, log, redirect)',
          'backend/app/routers/unsubscribe.py → GET /api/unsubscribe/status (lookup)',
          'backend/app/services/email_service.py → generate_unsubscribe_url(user_id)',
          'backend/app/services/reengagement.py → skips email_unsubscribed users, passes unsubscribe_url= to all drip calls',
          'backend/app/templates/emails/_footer.html → {% if unsubscribe_url %} block above copyright',
          'backend/app/main.py → app.include_router(unsubscribe_router)',
        ],
        frontend: [
          'frontend/src/pages/Unsubscribed.jsx → confirmation page (orange brand, support email link, back-to-dashboard button)',
          'frontend/src/App.js → <Route path="/unsubscribed" element={<Unsubscribed />} /> (public, no auth)',
        ],
        apis: [
          'GET /api/unsubscribe?uid=&token= — HMAC-verified, redirects to /unsubscribed',
          'GET /api/unsubscribe/status?uid= — returns {unsubscribed: bool}',
        ],
      },
      {
        date: 'May 19, 2026',
        name: 'Email Open Tracking',
        description: 'Invisible 1×1 GIF pixel appended to every outgoing HTML email to detect opens.\n\nArchitecture change in send_email(): replaced single _log_email_attempt() with a two-step pattern — _create_log_entry() pre-inserts a "pending" email_log row and returns str(ObjectId); _inject_tracking() appends the pixel tag before </body> using the log_id; after SMTP send, _update_log_entry() updates the row to "sent"/"failed"/"simulated". This gives us the log_id before sending so the pixel URL is baked into the email.\n\nemail_log rows now include: opened (bool, default False), opened_at (datetime, optional), clicked (bool), clicked_at (datetime), click_count (int).\n\nPixel endpoint uses $set with {$ne: true} guard so only the first load records opened_at — subsequent image reloads are no-ops.',
        backend: [
          'backend/app/routers/unsubscribe.py → GET /api/track/open/{log_id} — sets opened=True/opened_at, returns 1×1 transparent GIF',
          'backend/app/services/email_service.py → _create_log_entry() pre-insert with pending status',
          'backend/app/services/email_service.py → _update_log_entry() updates status after send',
          'backend/app/services/email_service.py → _inject_tracking() appends pixel + wraps clicks',
          'backend/app/services/email_service.py → send_email() refactored: pre-log → inject → send → update',
        ],
        frontend: ['—'],
        apis: ['GET /api/track/open/{log_id} — returns image/gif 1×1 transparent pixel'],
      },
      {
        date: 'May 19, 2026',
        name: 'Click Tracking',
        description: 'All href links pointing to the platform domain (settings.frontend_base_url) are automatically rewritten to click-tracking redirect URLs before the email is sent.\n\n_inject_tracking() uses re.sub on href="..." patterns — any URL starting with frontend_base_url is replaced with /api/track/click/{log_id}?url=<encoded>. External links (social icons, WhatsApp, etc.) are left untouched.\n\nClick endpoint: increments click_count via $inc, sets clicked=True + clicked_at on first click, then RedirectResponse to the decoded destination. Safety check: decoded URL must start with frontend_base_url — any other domain is blocked and redirected to homepage (no open redirect).\n\nData stored per email: clicked (bool), clicked_at (datetime of first click), click_count (int, incremented on every click).',
        backend: [
          'backend/app/routers/unsubscribe.py → GET /api/track/click/{log_id}?url= — validates domain, $inc click_count, $set clicked/clicked_at, RedirectResponse',
          'backend/app/services/email_service.py → _inject_tracking() wraps frontend_base_url hrefs via re.sub',
        ],
        frontend: ['—'],
        apis: ['GET /api/track/click/{log_id}?url= — validates URL, records click, redirects'],
      },
    ],
  },

  // ─────────── 📧 EMAIL BROADCAST SYSTEM ───────────
  {
    category: 'email_broadcast',
    title: 'Email Broadcast System (May 29, 2026)',
    icon: Mail,
    color: 'orange',
    features: [
      {
        name: 'Core Broadcast — Phase 1',
        description: 'Admin-initiated bulk email campaign system. Compose HTML email with subject, preheader (inbox preview text), heading, body HTML, and optional CTA button. Personalization tokens {{name}} and {{email}} replaced per recipient at send time.\n\nAnti-spam guardrails (server-side only, cannot be bypassed from UI):\n  • Max 3 broadcasts per day (resets at UTC midnight)\n  • Min 4-hour cooldown between sends\n  • Frequency cap: max 2 broadcast emails per user per 7 days\n  • SMTP throttle: 80 emails/minute (0.75s delay between sends)\n\nAll sends run as background asyncio tasks — no HTTP timeout risk. Strong reference pattern (_BROADCAST_TASKS set) prevents GC killing in-flight tasks.\n\nBroadcast status lifecycle: sending → sent / sent_with_errors / failed. History stored in email_broadcasts collection.',
        backend: [
          'backend/app/routers/email_broadcast.py (router prefix: /api/admin/email-broadcast)',
          'collection: email_broadcasts → {_id (UUID str), subject, audience, email_type, status, sent/failed/skipped counts, sent_by, created_at, sent_at}',
          'collection: email_log → existing, re-used with triggered_by = "eb_{broadcast_id}" for per-broadcast tracking',
          'backend/app/templates/emails/broadcast.html — generic campaign email template',
        ],
        frontend: [
          'frontend/src/components/admin/EmailBroadcastTab.jsx',
          'Admin Panel → Email Blast tab (sidebar: Emails & Comms group)',
        ],
        apis: [
          'GET  /api/admin/email-broadcast/audience-count?audience=all|enrolled|not_enrolled&email_type=...',
          'POST /api/admin/email-broadcast/preview — sends test email to admin inbox',
          'POST /api/admin/email-broadcast/send — queues broadcast (immediate or scheduled)',
          'GET  /api/admin/email-broadcast/history?limit=50',
          'POST /api/admin/email-broadcast/{id}/retry — retry failed/error broadcasts',
        ],
      },
      {
        name: 'Email Types & Audience Filtering — Phase 2',
        description: '5 email types: announcements, batch_news, promotions, job_alerts, greetings. Each user has email_preferences dict per type (default: all True). Audience count and actual send both filter by opted-in users — users who disabled a type are skipped.\n\nAudience segments:\n  • all — all active users\n  • enrolled — users with at least one active course enrollment\n  • not_enrolled — registered but no active course (cold leads)\n\nFilter query: {email_preferences.{type}: {$ne: False}} — backward compatible (missing field = include).',
        backend: [
          'backend/app/routers/email_broadcast.py → _get_audience_users(db, audience, email_type)',
          'backend/app/routers/user_profile.py → GET/PUT /api/user/email-preferences',
        ],
        frontend: [
          'frontend/src/components/admin/EmailBroadcastTab.jsx → EMAIL_TYPES constant + email type pill selector',
          'frontend/src/pages/ProfilePage.jsx → Email Preferences section (Job Settings tab)',
        ],
        apis: [
          'GET /api/user/email-preferences — returns {email_preferences: {announcements: bool, ...}}',
          'PUT /api/user/email-preferences — update per-type prefs (only boolean values accepted)',
        ],
      },
      {
        name: 'Template Library — Phase 2',
        description: 'Save any composed email as a named reusable template. Templates stored in email_templates collection. Load a template → fills the entire compose form (subject, preheader, heading, body_html, cta_text, cta_url, email_type). Delete templates. Duplicate name check on save (409 error).',
        backend: [
          'backend/app/routers/email_broadcast.py → GET/POST/DELETE /templates',
          'collection: email_templates → {_id (UUID), name, subject, preheader, heading, body_html, cta_text, cta_url, email_type, created_by, created_at}',
        ],
        frontend: [
          'frontend/src/components/admin/EmailBroadcastTab.jsx → Template Library collapsible section',
          '"Save as Template" button in composer → SaveTemplateDialog modal',
          '"Load" button on each template → fills composer fields',
        ],
        apis: [
          'GET    /api/admin/email-broadcast/templates',
          'POST   /api/admin/email-broadcast/templates',
          'DELETE /api/admin/email-broadcast/templates/{id}',
        ],
      },
      {
        name: 'Scheduled Send — Phase 2',
        description: 'Toggle "Schedule for later" in composer → datetime-local picker appears. schedule_at stored as UTC datetime in email_broadcasts doc with status=scheduled. Background loop (scheduled_broadcast_loop) runs every 60s on app startup, queries for status=scheduled + scheduled_at <= now, fires them as background tasks. Anti-spam checks run at schedule time (not fire time) to avoid silent skip at 3am.',
        backend: [
          'backend/app/routers/email_broadcast.py → scheduled_broadcast_loop() coroutine',
          'backend/app/main.py → app.state.email_broadcast_scheduler = asyncio.create_task(scheduled_broadcast_loop())',
          'status flow: scheduled → sending → sent/sent_with_errors/failed',
        ],
        frontend: [
          'frontend/src/components/admin/EmailBroadcastTab.jsx → scheduleEnabled toggle + datetime-local input',
          'Confirm dialog shows "Scheduled for: <date>" row when scheduling',
          'History table shows purple "scheduled" badge + scheduled datetime',
        ],
        apis: ['POST /api/admin/email-broadcast/send with body.schedule_at = ISO datetime'],
      },
      {
        name: 'Per-Broadcast Open/Click Analytics — Phase 3',
        description: 'Every email sent in a broadcast is logged with triggered_by = "eb_{broadcast_id}". History table shows open_rate% and click_rate% computed from email_log.\n\n"Opens" button in history table (visible for sent/sent_with_errors) opens the Opens Detail modal:\n  • Summary cards: Total Sent, Opened count, Clicked count, Open Rate%\n  • Searchable table: recipient name (looked up from users collection), email, Opened (✅/—), Clicked (✅/—), Opened At (IST), Clicked At (IST)\n  • Shows all 181 recipients, not just openers\n  • "Showing N of M recipients · Opens tracked via email pixel" footer\n\nRetry button: appears on sent_with_errors / failed broadcasts. Re-sends only to users who were skipped or failed in the original send.',
        backend: [
          'backend/app/routers/email_broadcast.py → GET /{broadcast_id}/opens',
          'Batch-lookup user names: db.users.find({email: {$in: emails}}, {email,first_name,last_name})',
          'backend/app/routers/email_broadcast.py → POST /{id}/retry — re-queries failed users',
        ],
        frontend: [
          'frontend/src/components/admin/EmailBroadcastTab.jsx → OpensModal component',
          '"Opens" button → handleViewOpens() → GET /{id}/opens → setOpensData()',
          'RateBadge component in history table for open_rate / click_rate',
        ],
        apis: [
          'GET  /api/admin/email-broadcast/{id}/opens — per-user open detail',
          'POST /api/admin/email-broadcast/{id}/retry',
        ],
      },
    ],
  },

  // ─────────── 🎨 ADMIN PANEL REDESIGN ───────────
  {
    category: 'admin_sidebar',
    title: 'Admin Panel Sidebar Redesign (May 29, 2026)',
    icon: Shield,
    color: 'slate',
    features: [
      {
        name: 'Collapsible Sidebar Navigation',
        description: 'Replaced 27 horizontal tabs with a fixed left sidebar (industry standard pattern — Vercel/Linear/Stripe/Supabase all use this). Research confirmed: horizontal tabs fail above 8 items.\n\nSidebar specs:\n  • 260px expanded / 68px icon-only collapsed\n  • Smooth CSS transition on collapse (200ms ease-in-out)\n  • Collapse state persisted to localStorage (remembered across sessions)\n  • Keyboard shortcut: Ctrl+B / Cmd+B toggles collapse\n  • Hover tooltips in icon-only mode (group labels + badge counts)\n  • Groups with chevron expand/collapse (independently toggled)\n  • Active item: orange left-accent border + bg-orange-500/15 highlight\n  • Auto-expands the group containing the active tab on load\n\n27 tabs grouped into 8 sections:\n  👥 People — Users, Payments, Enroll, Revenue\n  📚 Content — Courses, Blueprint, New Course Guide, Announcements\n  📧 Emails & Comms — Email Blast, Broadcast, Email Activity\n  💼 Jobs & Careers — Jobs, Certificates, Profiles\n  📊 Analytics — Analytics, Activity, Performance\n  🖥️ System — Server, Network, Health Monitor, Security\n  🎨 Creative — Promo Poster, Achievement\n  🎟️ Support — Tickets, Agents, Projects\n  ⚙️ Admin — Team (super_admin only)\n\nMobile: slide-in drawer with black/blur backdrop overlay. MobileMenuButton (hamburger) fixed top-left, hidden on lg+.\n\nStats cards now only shown on People-group tabs (Users/Payments/Enroll/Revenue) — not globally on every tab load.\n\nRBAC: same ROLE_TABS logic as before — groups with no visible items for the current role are hidden entirely.',
        backend: ['—'],
        frontend: [
          'frontend/src/components/admin/AdminSidebar.jsx — NEW FILE (sidebar + MobileMenuButton)',
          'frontend/src/pages/AdminDashboard.jsx — replaced <Tabs> layout with sidebar + conditional render',
          'NAV_GROUPS config in AdminSidebar.jsx (data-driven, mirrors ROLE_TABS)',
          'All 27 tab content components UNCHANGED — zero regression risk',
        ],
        apis: ['—'],
      },
    ],
  },
];

// =====================================================================
// PRICING TABLE — single source of truth
// =====================================================================
const PRICING_TABLE = [
  { sku: 'ALL_ACCESS', name: 'All Access Bundle (13 courses + 3 exams)', inr: 4999, usd: 149, notes: 'Excludes Mock Tests & Projects. Credit applied from past INR purchases for Indian users.' },
  { sku: 'RH124', name: 'RHCSA I - Red Hat System Administration I', inr: 999, usd: 45, notes: 'Linux tier' },
  { sku: 'RH134', name: 'RHCSA II - Red Hat System Administration II', inr: 999, usd: 45, notes: 'Linux tier' },
  { sku: 'SVRADM', name: 'Server Administration', inr: 799, usd: 35, notes: 'DevOps tier' },
  { sku: 'DOCKER', name: 'Docker Containerization', inr: 799, usd: 35, notes: 'DevOps tier' },
  { sku: 'ANSIBLE', name: 'Ansible Automation Platform', inr: 799, usd: 35, notes: 'DevOps tier' },
  { sku: 'KUBERNETES', name: 'Kubernetes', inr: 799, usd: 35, notes: 'DevOps tier' },
  { sku: 'TERRAFORM', name: 'Terraform', inr: 799, usd: 35, notes: 'DevOps tier' },
  { sku: 'GIT', name: 'Git & GitHub', inr: 499, usd: 19, notes: 'Basic tier' },
  { sku: 'PYTHON', name: 'Python Programming', inr: 499, usd: 19, notes: 'Programming tier' },
  { sku: 'C_LANG', name: 'C Programming', inr: 499, usd: 19, notes: 'Programming tier' },
  { sku: 'CPP', name: 'C++ Programming', inr: 499, usd: 19, notes: 'Programming tier' },
  { sku: 'JAVA', name: 'Core Java', inr: 499, usd: 19, notes: 'Programming tier' },
  { sku: 'MYSQL', name: 'MySQL/MariaDB', inr: 499, usd: 19, notes: 'Database tier' },
  { sku: 'EXAM_RHCSA', name: 'RHCSA Practice Exam Attempt', inr: 499, usd: 19, notes: 'Included in All Access' },
  { sku: 'EXAM_DO188', name: 'DO188 Practice Exam Attempt', inr: 499, usd: 19, notes: 'Included in All Access' },
  { sku: 'EXAM_EX294', name: 'EX294 Ansible Practice Exam Attempt', inr: 499, usd: 19, notes: 'Included in All Access' },
  { sku: 'MOCK_TESTS', name: 'Mock Tests Bundle', inr: 499, usd: 19, notes: '❌ Excluded from All Access' },
  { sku: 'PROJECT_<id>', name: 'Per-Project Access', inr: 99, usd: 2, notes: '❌ Excluded from All Access. Flat rate per project.' },
];

// =====================================================================
// BUSINESS RULES — the "don't break these" list
// =====================================================================
const BUSINESS_RULES = [
  '✅ All Access INCLUDES 13 courses + 3 exam attempts (RHCSA/DO188/EX294). Total 16 items.',
  '❌ All Access EXCLUDES Mock Tests (₹499/$19) and Real-World Projects Hub (₹99/$2 per project). These are sold separately — do NOT bundle them.',
  '💰 Indian users get credit for past INR course purchases against All Access (min floor ₹499, no max cap).',
  '🌍 International users charged flat $149 USD for All Access. NO credit applied (keeps logic simple).',
  '💳 Razorpay bills international cards in USD (cents), Indian cards in INR (paise). This requires International Payments + USD enabled on Razorpay account.',
  '🔐 (user_id, lab_long_id) is unique in db.progress — repeat attempts cannot inflate leaderboard stats.',
  '🛡️ Leaderboard shows only Top 5 names publicly. Rivals are anonymous. Total student count is hidden from all student-facing UI.',
  '📝 Admin Panel is the only surface where raw user data (emails, full payment lists) is visible. Never expose these to students.',
  '♻️ Hot reload enabled for both backend and frontend. Supervisor restart needed ONLY for .env changes or new pip/yarn installs.',
  '🏷️ Course catalog is YAML-driven (backend/specs/). Course titles dictionary in specs_loader.py. Adding a new course = new YAML folder + title entry + pricing entries.',
];

// =====================================================================
// COLOR MAP
// =====================================================================
const COLORS = {
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-300', iconBg: 'bg-emerald-500/20' },
  indigo: { bg: 'bg-indigo-500/10', border: 'border-indigo-500/20', text: 'text-indigo-300', iconBg: 'bg-indigo-500/20' },
  slate: { bg: 'bg-slate-500/10', border: 'border-slate-500/20', text: 'text-slate-300', iconBg: 'bg-slate-500/20' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-300', iconBg: 'bg-amber-500/20' },
  yellow: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', text: 'text-yellow-300', iconBg: 'bg-yellow-500/20' },
  rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/20', text: 'text-rose-300', iconBg: 'bg-rose-500/20' },
  purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/20', text: 'text-purple-300', iconBg: 'bg-purple-500/20' },
  fuchsia: { bg: 'bg-fuchsia-500/10', border: 'border-fuchsia-500/20', text: 'text-fuchsia-300', iconBg: 'bg-fuchsia-500/20' },
  sky: { bg: 'bg-sky-500/10', border: 'border-sky-500/20', text: 'text-sky-300', iconBg: 'bg-sky-500/20' },
  orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/20', text: 'text-orange-300', iconBg: 'bg-orange-500/20' },
  cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-300', iconBg: 'bg-cyan-500/20' },
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/20', text: 'text-violet-300', iconBg: 'bg-violet-500/20' },
};

// =====================================================================
// UI
// =====================================================================
export const BlueprintTab = () => {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set(['auth'])); // first section open by default
  const [view, setView] = useState('features'); // 'features' | 'pricing' | 'rules'

  const toggle = (cat) => {
    setExpanded((prev) => {
      const s = new Set(prev);
      s.has(cat) ? s.delete(cat) : s.add(cat);
      return s;
    });
  };

  const q = query.trim().toLowerCase();
  const filteredBlueprint = useMemo(() => {
    if (!q) return BLUEPRINT;
    return BLUEPRINT.map((section) => {
      const matchingFeatures = section.features.filter((f) => {
        const hay = [
          f.name, f.description, ...(f.backend || []), ...(f.frontend || []), ...(f.apis || []),
        ].join(' ').toLowerCase();
        return hay.includes(q);
      });
      if (matchingFeatures.length === 0 && !section.title.toLowerCase().includes(q)) return null;
      return { ...section, features: matchingFeatures.length ? matchingFeatures : section.features };
    }).filter(Boolean);
  }, [q]);

  return (
    <div className="space-y-4" data-testid="blueprint-tab">
      {/* Header + Search */}
      <Card className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Rocket className="w-5 h-5 text-indigo-300" />
            Portal Blueprint — Feature Map & Reference
          </CardTitle>
          <p className="text-slate-400 text-sm">
            Har feature, file, API aur pricing ek jagah. Search karo, expand karo, jo chahiye turant mil jayega.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* View Toggle */}
          <div className="flex gap-2 flex-wrap">
            {[
              { id: 'features', label: `Features (${BLUEPRINT.reduce((a, s) => a + s.features.length, 0)})`, icon: Rocket },
              { id: 'pricing', label: `Pricing (${PRICING_TABLE.length} SKUs)`, icon: Wallet },
              { id: 'rules', label: `Business Rules (${BUSINESS_RULES.length})`, icon: Shield },
            ].map((v) => (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                data-testid={`blueprint-view-${v.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  view === v.id
                    ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/30'
                    : 'bg-white/5 text-slate-300 hover:bg-white/10 border border-white/10'
                }`}
              >
                <v.icon className="w-4 h-4" />
                {v.label}
              </button>
            ))}
          </div>

          {/* Search only for features view */}
          {view === 'features' && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search feature, file path, API route... e.g. razorpay, leaderboard, admin.py"
                data-testid="blueprint-search"
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* FEATURES VIEW */}
      {view === 'features' && filteredBlueprint.map((section) => {
        const Icon = section.icon;
        const c = COLORS[section.color] || COLORS.slate;
        const isOpen = expanded.has(section.category) || !!q; // auto-expand during search
        return (
          <Card key={section.category} className={`${c.bg} ${c.border} backdrop-blur`}>
            <button
              onClick={() => toggle(section.category)}
              className="w-full text-left"
              data-testid={`blueprint-section-${section.category}`}
            >
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.iconBg}`}>
                    <Icon className={`w-5 h-5 ${c.text}`} />
                  </div>
                  <div>
                    <CardTitle className="text-white">{section.title}</CardTitle>
                    <p className={`text-xs ${c.text} opacity-80`}>{section.features.length} features</p>
                  </div>
                </div>
                {isOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
              </CardHeader>
            </button>
            {isOpen && (
              <CardContent className="space-y-3">
                {section.features.map((f, i) => (
                  <div
                    key={i}
                    data-testid={`blueprint-feature-${section.category}-${i}`}
                    className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-2 hover:bg-white/[0.07] transition"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-white font-semibold">{f.name}</h4>
                    </div>
                    <p className="text-slate-300 text-sm whitespace-pre-line">{f.description}</p>
                    <div className="grid md:grid-cols-2 gap-2 pt-2">
                      {f.backend && f.backend.length > 0 && f.backend[0] !== '—' && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold mb-1">Backend</div>
                          <ul className="space-y-0.5">
                            {f.backend.map((b, j) => (
                              <li key={j} className="text-xs text-slate-300 font-mono bg-black/30 rounded px-2 py-1">{b}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {f.frontend && f.frontend.length > 0 && f.frontend[0] !== '—' && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-sky-400 font-bold mb-1">Frontend</div>
                          <ul className="space-y-0.5">
                            {f.frontend.map((b, j) => (
                              <li key={j} className="text-xs text-slate-300 font-mono bg-black/30 rounded px-2 py-1">{b}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    {f.apis && f.apis.length > 0 && f.apis[0] !== '—' && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-1 mt-2">API Endpoints</div>
                        <div className="flex flex-wrap gap-1.5">
                          {f.apis.map((a, j) => (
                            <Badge key={j} className="bg-amber-500/15 text-amber-300 border border-amber-500/30 font-mono text-[10px]">{a}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        );
      })}

      {view === 'features' && filteredBlueprint.length === 0 && (
        <div className="text-center py-12 text-slate-400">No features match "{query}". Try a different keyword.</div>
      )}

      {/* PRICING VIEW */}
      {view === 'pricing' && (
        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-300" />
              Pricing Table — All SKUs
            </CardTitle>
            <p className="text-xs text-slate-400">USD conversion rate: 1 USD ≈ ₹90. International users billed in USD via Razorpay (cents).</p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="blueprint-pricing-table">
              <thead>
                <tr className="text-left text-slate-400 border-b border-white/10">
                  <th className="pb-2 pr-4 font-medium">SKU</th>
                  <th className="pb-2 pr-4 font-medium">Name</th>
                  <th className="pb-2 pr-4 font-medium text-right">INR</th>
                  <th className="pb-2 pr-4 font-medium text-right">USD</th>
                  <th className="pb-2 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                {PRICING_TABLE.map((p) => (
                  <tr key={p.sku} className="border-b border-white/5 hover:bg-white/[0.03]">
                    <td className="py-2 pr-4"><code className="text-indigo-300 text-xs">{p.sku}</code></td>
                    <td className="py-2 pr-4 text-slate-200">{p.name}</td>
                    <td className="py-2 pr-4 text-right text-emerald-300 font-mono font-bold">₹{p.inr.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right text-sky-300 font-mono font-bold">${p.usd}</td>
                    <td className="py-2 text-xs text-slate-400">{p.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* BUSINESS RULES VIEW */}
      {view === 'rules' && (
        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-rose-300" />
              Business Rules — "Don't Break These"
            </CardTitle>
            <p className="text-xs text-slate-400">These are the invariants that power the portal. Every new feature/fix must respect them.</p>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2.5" data-testid="blueprint-rules-list">
              {BUSINESS_RULES.map((rule, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-black/20 text-slate-200 text-sm"
                >
                  <span className="text-slate-500 font-mono text-xs flex-shrink-0 mt-0.5">{String(i + 1).padStart(2, '0')}.</span>
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BlueprintTab;
